import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import PresetSelector from './components/PresetSelector';
import AutotuneControls from './components/AutotuneControls';
import BeatImporter from './components/BeatImporter';
import { buildAutotuneChain, updateAutotuneParams } from './components/AutotuneDSP';
import { detectKeyFromAudioBuffer, scaleNotesForKey } from '../../lib/key-detect';

import AudioMeter from './components/AudioMeter';
import DeviceSelector from './components/DeviceSelector';
import MicReadinessPanel from './components/MicReadinessPanel';
import MetronomeControl from './components/MetronomeControl';
import DemoCounter from './components/DemoCounter';
import ProToolsTransport from './components/ProToolsTransport';
import TrackMixer from './components/TrackMixer';
import TakeManager from './components/TakeManager';
import ExportPanel from './components/ExportPanel';
import ProjectTemplates from './components/ProjectTemplates';
import SessionNotes from './components/SessionNotes';
import TimeMarkers from './components/TimeMarkers';
import VocalFxChain from './components/VocalFxChain';
import TutorialOverlay from './components/TutorialOverlay';
import AutoSaveIndicator from './components/AutoSaveIndicator';
import WaveformZoom from './components/WaveformZoom';

import ChannelFaders from './components/ChannelFaders';
import ProToolsEditWindow from './components/ProToolsEditWindow';
import SubscriptionPaywall from './components/SubscriptionPaywall';

const audioBufferToWav = (buffer, bitDepth = 16) => {
  const numberOfChannels = buffer?.numberOfChannels;
  const sampleRate = buffer?.sampleRate;
  const format = 1; // PCM
  const bytesPerSample = bitDepth / 8;

  let length = buffer?.length * numberOfChannels * bytesPerSample;
  let result = new ArrayBuffer(44 + length);
  let view = new DataView(result);

  const writeString = (offset, string) => {
    for (let i = 0; i < string?.length; i++) {
      view?.setUint8(offset + i, string?.charCodeAt(i));
    }
  };

  let offset = 0;
  // RIFF identifier
  writeString(offset, 'RIFF'); offset += 4;
  // file length
  view?.setUint32(offset, 36 + length, true); offset += 4;
  // RIFF type
  writeString(offset, 'WAVE'); offset += 4;
  // format chunk identifier
  writeString(offset, 'fmt '); offset += 4;
  // format chunk length
  view?.setUint32(offset, 16, true); offset += 4;
  // sample format (raw)
  view?.setUint16(offset, format, true); offset += 2;
  // channel count
  view?.setUint16(offset, numberOfChannels, true); offset += 2;
  // sample rate
  view?.setUint32(offset, sampleRate, true); offset += 4;
  // byte rate (sample rate * block align)
  view?.setUint32(offset, sampleRate * numberOfChannels * bytesPerSample, true); offset += 4;
  // block align (channel count * bytes per sample)
  view?.setUint16(offset, numberOfChannels * bytesPerSample, true); offset += 2;
  // bits per sample
  view?.setUint16(offset, bitDepth, true); offset += 2;
  // data chunk identifier
  writeString(offset, 'data'); offset += 4;
  // data chunk length
  view?.setUint32(offset, length, true); offset += 4;

  // write the PCM samples
  let index = 44;
  for (let i = 0; i < buffer?.length; i++) {
    for (let channel = 0; channel < numberOfChannels; channel++) {
      let sample = buffer?.getChannelData(channel)?.[i];
      // clamp
      sample = Math.max(-1, Math.min(1, sample));
      if (bitDepth === 24) {
        const value = Math.round(sample < 0 ? sample * 0x800000 : sample * 0x7FFFFF);
        view?.setUint8(index, value & 0xFF);
        view?.setUint8(index + 1, (value >> 8) & 0xFF);
        view?.setUint8(index + 2, (value >> 16) & 0xFF);
        index += 3;
      } else {
        // scale to 16-bit
        sample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
        view?.setInt16(index, sample, true);
        index += 2;
      }
    }
  }

  return new Blob([result], { type: 'audio/wav' });
};

const DEFAULT_MARKERS = [
  { id: 'intro', label: 'Intro', color: '#6366f1', time: 0 },
  { id: 'verse1', label: 'Verse 1', color: '#22c55e', time: null },
  { id: 'chorus1', label: 'Hook', color: '#f59e0b', time: null },
  { id: 'verse2', label: 'Verse 2', color: '#22c55e', time: null },
  { id: 'bridge', label: 'Bridge', color: '#ec4899', time: null },
  { id: 'outro', label: 'Outro', color: '#ef4444', time: null },
];

const BACKEND_API_URL = (import.meta.env?.VITE_API_URL || 'http://localhost:4000').replace(/\/$/, '');
const BACKEND_PUBLIC_URL = (import.meta.env?.VITE_PUBLIC_URL || BACKEND_API_URL.replace(/\/api\/?$/i, '')).replace(/\/$/, '');
const REQUIRE_SUBSCRIPTION = import.meta.env?.VITE_REQUIRE_SUBSCRIPTION === 'true';
const DEFAULT_VOCAL_FX_SETTINGS = {
  enabled: false,
  highpassHz: 80,
  compression: 25,
  presence: 35,
  reverb: 10,
  delay: 0,
};

const makeChannels = () => [
  { id: 'lead', recorded: false, isRecording: false, isPlaying: false, duration: null, filePath: null, muted: false, soloed: false, volume: 1.0, pan: 0, color: '#ef4444', customName: null },
  { id: 'double', recorded: false, isRecording: false, isPlaying: false, duration: null, filePath: null, muted: false, soloed: false, volume: 1.0, pan: 0, color: '#f97316', customName: null },
  { id: 'adlib', recorded: false, isRecording: false, isPlaying: false, duration: null, filePath: null, muted: false, soloed: false, volume: 1.0, pan: 0, color: '#6366f1', customName: null },
  { id: 'extra', recorded: false, isRecording: false, isPlaying: false, duration: null, filePath: null, muted: false, soloed: false, volume: 1.0, pan: 0, color: '#22c55e', customName: null },
];

const getSupportedRecordingMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return '';
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/mpeg',
  ];

  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) || '';
};

const generateWaveformFromBuffer = (audioBuffer, samples = 90) => {
  const rawData = audioBuffer?.getChannelData?.(0);
  if (!rawData?.length) return [];

  const blockSize = Math.max(1, Math.floor(rawData.length / samples));
  const waveform = [];
  for (let i = 0; i < samples; i++) {
    const start = i * blockSize;
    let sum = 0;
    for (let j = 0; j < blockSize && start + j < rawData.length; j++) {
      sum += rawData[start + j] ** 2;
    }
    waveform.push(Math.sqrt(sum / blockSize));
  }

  const max = Math.max(...waveform, 0.001);
  return waveform.map((value) => value / max);
};

const makeTakeId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const parseChannelKey = (channelKey) => {
  const [sectionId, ...channelIdParts] = channelKey.split('-');
  return {
    sectionId,
    channelId: channelIdParts.join('-'),
  };
};

const getFormatBitDepth = (format) => format === 'wav24' ? 24 : 16;

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

const dataUrlToBlob = (dataUrl) => {
  const [header, base64] = dataUrl.split(',');
  const mime = header?.match(/data:(.*?);base64/)?.[1] || 'application/octet-stream';
  const binary = atob(base64 || '');
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
};

const RECOVERY_DB_NAME = 'makingitmix-recording-recovery';
const RECOVERY_STORE_NAME = 'snapshots';
const VERSION_STORE_NAME = 'versions';
const RECOVERY_KEY = 'latest';

const openRecoveryDb = () => new Promise((resolve, reject) => {
  if (typeof indexedDB === 'undefined') {
    resolve(null);
    return;
  }
  const request = indexedDB.open(RECOVERY_DB_NAME, 2);
  request.onupgradeneeded = () => {
    const db = request.result;
    if (!db.objectStoreNames.contains(RECOVERY_STORE_NAME)) {
      db.createObjectStore(RECOVERY_STORE_NAME);
    }
    if (!db.objectStoreNames.contains(VERSION_STORE_NAME)) {
      db.createObjectStore(VERSION_STORE_NAME);
    }
  };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error);
});

const withRecoveryStore = async (mode, callback) => {
  const db = await openRecoveryDb();
  if (!db) return null;
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(RECOVERY_STORE_NAME, mode);
    const store = transaction.objectStore(RECOVERY_STORE_NAME);
    const request = callback(store);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    transaction.oncomplete = () => db.close();
    transaction.onerror = () => {
      db.close();
      reject(transaction.error);
    };
  });
};

const saveRecoverySnapshot = (snapshot) =>
  withRecoveryStore('readwrite', (store) => store.put(snapshot, RECOVERY_KEY));

const loadRecoverySnapshot = () =>
  withRecoveryStore('readonly', (store) => store.get(RECOVERY_KEY));

const clearRecoverySnapshot = () =>
  withRecoveryStore('readwrite', (store) => store.delete(RECOVERY_KEY));

const withVersionStore = async (mode, callback) => {
  const db = await openRecoveryDb();
  if (!db) return null;
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(VERSION_STORE_NAME, mode);
    const store = transaction.objectStore(VERSION_STORE_NAME);
    const request = callback(store);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    transaction.oncomplete = () => db.close();
    transaction.onerror = () => {
      db.close();
      reject(transaction.error);
    };
  });
};

const saveProjectVersion = (snapshot) =>
  withVersionStore('readwrite', (store) => store.put(snapshot, snapshot.id));

const loadProjectVersions = () =>
  withVersionStore('readonly', (store) => store.getAll());

const deleteProjectVersion = (id) =>
  withVersionStore('readwrite', (store) => store.delete(id));

const stripAudioBuffer = (info) => {
  if (!info) return null;
  const { audioBuffer, ...serializable } = info;
  return serializable;
};

const getBackendAuthToken = () => localStorage.getItem('auth_token');

const parseSessionData = (value) => {
  if (!value) return {};
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch (error) {
      return {};
    }
  }
  return value;
};

const resolveAssetUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url) || url.startsWith('blob:') || url.startsWith('data:')) return url;
  if (url.startsWith('/uploads/')) return `${BACKEND_PUBLIC_URL}${url}`;
  return `${BACKEND_API_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

const normalizeClipSettings = (updates = {}, duration = 0) => {
  const maxDuration = Math.max(0, Number(duration) || 0);
  const nextStart = Math.max(0, Math.min(Number(updates.clipStart ?? 0) || 0, maxDuration));
  const requestedEnd = updates.clipEnd ?? maxDuration;
  const nextEnd = Math.max(nextStart, Math.min(Number(requestedEnd) || maxDuration, maxDuration));
  const nextOffset = Math.max(0, Number(updates.clipOffset ?? 0) || 0);

  return {
    clipStart: nextStart,
    clipEnd: nextEnd,
    clipOffset: nextOffset,
  };
};

const getTakeClipRange = (take = {}) => {
  const duration = Math.max(0, Number(take?.duration) || 0);
  const clipStart = Math.max(0, Math.min(Number(take?.clipStart ?? 0) || 0, duration));
  const clipEnd = Math.max(clipStart, Math.min(Number(take?.clipEnd ?? duration) || duration, duration));
  const clipOffset = Math.max(0, Number(take?.clipOffset ?? 0) || 0);

  return {
    clipStart,
    clipEnd,
    clipOffset,
    timelineStart: clipOffset,
    timelineEnd: clipOffset + Math.max(0, clipEnd - clipStart),
  };
};

const hasTakeAudioAsset = (take = {}) =>
  Boolean(take?.blobUrl || take?.remoteUrl || take?.remotePath || take?.filePath);

const RecordingStudio = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, hasProAccess, isProUser, authProvider } = useAuth();
  const [selectedPreset, setSelectedPreset] = useState('hiphop');
  const [simpleMode, setSimpleMode] = useState(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [autotuneEnabled, setAutotuneEnabled] = useState(false);
  const [retuneSpeed, setRetuneSpeed] = useState(50);
  const [autotuneIntensity, setAutotuneIntensity] = useState(100);
  const [autotuneWetMix, setAutotuneWetMix] = useState(80);
  const [vocalFxSettings, setVocalFxSettings] = useState(DEFAULT_VOCAL_FX_SETTINGS);
  const [beatVolumeState, setBeatVolumeState] = useState(80);
  const [channelFaderVolumes, setChannelFaderVolumes] = useState({
    beat: 0.8,
    verse1: 1.0,
    verse2: 1.0,
    verse3: 1.0,
    hook: 1.0,
  });
  const channelFaderVolumesRef = useRef({ beat: 0.8, verse1: 1.0, verse2: 1.0, verse3: 1.0, hook: 1.0 });
  const [beatInfo, setBeatInfo] = useState(null);

  // Auto-key detection listener — beat decode dispatches makingitmix:key-detected
  React.useEffect(() => {
    const handler = (e) => {
      const { label, scaleNotes } = e?.detail || {};
      if (!label) return;
      setBeatInfo(prev => ({ ...(prev || {}), key: label, keyScaleNotes: scaleNotes }));
    };
    window.addEventListener('makingitmix:key-detected', handler);
    return () => window.removeEventListener('makingitmix:key-detected', handler);
  }, []);
  const [isBeatPlaying, setIsBeatPlaying] = useState(false);
  const [activeTab, setActiveTab] = useState('verse1');
  const [isRecording, setIsRecording] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [sessionTitle, setSessionTitle] = useState(location?.state?.sessionName || 'Untitled Session');
  const [saving, setSaving] = useState(false);
  const [shareSaving, setShareSaving] = useState(false);
  const [shareLinks, setShareLinks] = useState([]);
  const [sharesLoading, setSharesLoading] = useState(false);
  const [shareRevokingId, setShareRevokingId] = useState(null);
  const [feedbackComments, setFeedbackComments] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [saveNotice, setSaveNotice] = useState(null);
  const [cloudExportJobs, setCloudExportJobs] = useState([]);
  const [cloudExportsLoading, setCloudExportsLoading] = useState(false);
  const [loadingSession, setLoadingSession] = useState(false);
  const [showCloudSessionBrowser, setShowCloudSessionBrowser] = useState(false);
  const [cloudSessions, setCloudSessions] = useState([]);
  const [cloudSessionsLoading, setCloudSessionsLoading] = useState(false);
  const [cloudSessionError, setCloudSessionError] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [sessionVersions, setSessionVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionBusyId, setVersionBusyId] = useState('');
  const [selectedDevice, setSelectedDevice] = useState('');
  const [metronomeEnabled, setMetronomeEnabled] = useState(false);
  const [countInEnabled, setCountInEnabled] = useState(true);
  const [countInBars, setCountInBars] = useState(1);
  const [countInRemaining, setCountInRemaining] = useState(null);
  const [inputLevel, setInputLevel] = useState(0);
  const [micPermissionError, setMicPermissionError] = useState(null);
  const [recordingError, setRecordingError] = useState(null);
  const [autoMixing, setAutoMixing] = useState(false);
  const [monitoringEnabled, setMonitoringEnabled] = useState(false);
  const [manualLatencyMs, setManualLatencyMs] = useState(() => {
    const saved = localStorage.getItem('studio_latency_compensation_ms');
    return saved !== null ? Number(saved) : 0;
  });
  const [showLatencyFader, setShowLatencyFader] = useState(false);

  const [currentTime, setCurrentTime] = useState(0);
  const [armedChannel, setArmedChannel] = useState(null);
  const [isPunchInEnabled, setIsPunchInEnabled] = useState(false);
  const [isLoopEnabled, setIsLoopEnabled] = useState(false);
  const [punchInPoint, setPunchInPoint] = useState(null);
  const [punchOutPoint, setPunchOutPoint] = useState(null);
  const [loopStartPoint, setLoopStartPoint] = useState(0);
  const [loopEndPoint, setLoopEndPoint] = useState(16);
  const [seekTime, setSeekTime] = useState(null);
  const [recordedWaveforms, setRecordedWaveforms] = useState({});

  const [auditionChannels, setAuditionChannels] = useState({});

  const [timeMarkers, setTimeMarkers] = useState(DEFAULT_MARKERS);
  const [waveformZoom, setWaveformZoom] = useState(1);
  const [snapEnabled, setSnapEnabled] = useState(false);
  const [gridSize, setGridSize] = useState(1);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [sessionNotes, setSessionNotes] = useState('');
  const [sessionLyrics, setSessionLyrics] = useState('');
  const [takes, setTakes] = useState({});
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [rightPanel, setRightPanel] = useState('controls');
  const [recoverySnapshot, setRecoverySnapshot] = useState(null);
  const [recoveryBusy, setRecoveryBusy] = useState(false);

  const handleAutoSave = async (data) => {
    if (!user || !currentSessionId) return;
    const sessionPayload = {
      ...data,
      beatInfo: stripAudioBuffer(data?.beatInfo),
    };
    const backendToken = getBackendAuthToken();
    try {
      if (backendToken) {
        const response = await fetch(`${BACKEND_API_URL}/sessions/save`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${backendToken}`,
          },
          body: JSON.stringify({
            id: currentSessionId,
            title: sessionTitle,
            preset: selectedPreset,
            autotune_enabled: autotuneEnabled,
            retune_speed: retuneSpeed,
            session_data: sessionPayload,
          }),
        });
        if (!response.ok) {
          const message = await response.text();
          throw new Error(message || 'Backend auto-save failed');
        }
        return;
      }

      await supabase?.from('audio_sessions')?.update({
        session_data: sessionPayload,
        updated_at: new Date()?.toISOString(),
      })?.eq('id', currentSessionId);
    } catch (error) {
      console.error('Auto-save error:', error);
    }
  };

  // Audio refs
  const audioContextRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordingMimeTypeRef = useRef('audio/webm');
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const audioBuffersRef = useRef({});
  const recordingStartTimeRef = useRef(null);
  const retryCountRef = useRef(0);
  const deviceInitializedRef = useRef(false);
  const audioPlayersRef = useRef({});
  const audioPlayerTimeoutsRef = useRef({});
  const beatImporterRef = useRef(null);
  const autotuneSourceRef = useRef(null);
  const autotuneDestRef = useRef(null);
  const autotuneNodesRef = useRef([]);
  const autotuneChainRef = useRef(null);
  const processedStreamRef = useRef(null);
  const connectedAudioElementsRef = useRef(new WeakSet());
  const sectionGainNodesRef = useRef({ verse1: [], verse2: [], verse3: [], hook: [] });
  const trackVolumeGainNodesRef = useRef({});
  const trackPanNodesRef = useRef({});

  const bluetoothCompensationRef = useRef(null);
  const monitoringGainRef = useRef(null);
  const isBluetoothDeviceRef = useRef(false);
  const latencyCompensationMsRef = useRef(0);
  const pitchProcessorRef = useRef(null);
  const pitchShiftBufferRef = useRef(null);
  const autotuneCleanupRef = useRef(null);

  const metronomeCtxRef = useRef(null);
  const metronomeIntervalRef = useRef(null);
  const metronomeBeatRef = useRef(0);
  const countInTimeoutsRef = useRef([]);
  const mixPlaybackSignatureRef = useRef('');

  const playheadIntervalRef = useRef(null);
  const playStartWallRef = useRef(null);
  const playStartTimeRef = useRef(0);
  const punchOutTimeoutRef = useRef(null);
  const micSourceRef = useRef(null);
  const meterDataRef = useRef(null);
  const inputLevelRef = useRef(0);
  const meterLastUiUpdateRef = useRef(0);

  // Add this block - Session import ref
  const sessionImportRef = useRef(null);
  const loadedSessionIdRef = useRef(null);
  const recoveryTimerRef = useRef(null);
  const recoverySignatureRef = useRef('');
  // End of added block

  const [verse1Channels, setVerse1Channels] = useState(makeChannels());
  const [verse2Channels, setVerse2Channels] = useState(makeChannels());
  const [verse3Channels, setVerse3Channels] = useState(makeChannels());
  const [hookChannels, setHookChannels] = useState(makeChannels());

  const autoSaveData = {
    verse1: verse1Channels,
    verse2: verse2Channels,
    verse3: verse3Channels,
    hook: hookChannels,
    beatInfo,
    selectedPreset,
    autotuneEnabled,
    retuneSpeed,
    autotuneIntensity,
    autotuneWetMix,
    vocalFxSettings,
    countInEnabled,
    countInBars,
    loopStartPoint,
    loopEndPoint,
    punchInPoint,
    punchOutPoint,
    isPunchInEnabled,
    isLoopEnabled,
    waveformZoom,
    snapEnabled,
    gridSize,
    timeMarkers,
    sessionNotes,
    sessionLyrics,
    takes,
    recordedWaveforms,
  };

  const cloneHistoryValue = (value) => JSON.parse(JSON.stringify(value));

  const createStudioSnapshot = () => ({
    verse1Channels: cloneHistoryValue(verse1Channels),
    verse2Channels: cloneHistoryValue(verse2Channels),
    verse3Channels: cloneHistoryValue(verse3Channels),
    hookChannels: cloneHistoryValue(hookChannels),
    takes: cloneHistoryValue(takes),
    recordedWaveforms: cloneHistoryValue(recordedWaveforms),
    channelFaderVolumes: cloneHistoryValue(channelFaderVolumes),
    armedChannel: cloneHistoryValue(armedChannel),
    loopStartPoint,
    loopEndPoint,
    punchInPoint,
    punchOutPoint,
    isPunchInEnabled,
    isLoopEnabled,
    waveformZoom,
    snapEnabled,
    gridSize,
    autotuneIntensity,
    autotuneWetMix,
    vocalFxSettings: cloneHistoryValue(vocalFxSettings),
    timeMarkers: cloneHistoryValue(timeMarkers),
    countInEnabled,
    countInBars,
  });

  const applyStudioSnapshot = (snapshot) => {
    if (!snapshot) return;
    stopAllTrackPlayback();
    setVerse1Channels(snapshot.verse1Channels || makeChannels());
    setVerse2Channels(snapshot.verse2Channels || makeChannels());
    setVerse3Channels(snapshot.verse3Channels || makeChannels());
    setHookChannels(snapshot.hookChannels || makeChannels());
    setTakes(snapshot.takes || {});
    setRecordedWaveforms(snapshot.recordedWaveforms || {});
    setChannelFaderVolumes(snapshot.channelFaderVolumes || channelFaderVolumes);
    channelFaderVolumesRef.current = snapshot.channelFaderVolumes || channelFaderVolumesRef.current;
    setArmedChannel(snapshot.armedChannel || null);
    if (snapshot.loopStartPoint != null) setLoopStartPoint(snapshot.loopStartPoint);
    if (snapshot.loopEndPoint != null) setLoopEndPoint(snapshot.loopEndPoint);
    setPunchInPoint(snapshot.punchInPoint ?? null);
    setPunchOutPoint(snapshot.punchOutPoint ?? null);
    setIsPunchInEnabled(Boolean(snapshot.isPunchInEnabled));
    setIsLoopEnabled(Boolean(snapshot.isLoopEnabled));
    if (snapshot.countInEnabled != null) setCountInEnabled(Boolean(snapshot.countInEnabled));
    if (snapshot.countInBars != null) setCountInBars(snapshot.countInBars);
    if (snapshot.autotuneIntensity != null) setAutotuneIntensity(snapshot.autotuneIntensity);
    if (snapshot.autotuneWetMix != null) setAutotuneWetMix(snapshot.autotuneWetMix);
    if (snapshot.waveformZoom != null) setWaveformZoom(snapshot.waveformZoom);
    if (snapshot.snapEnabled != null) setSnapEnabled(Boolean(snapshot.snapEnabled));
    if (snapshot.gridSize != null) setGridSize(snapshot.gridSize);
    if (snapshot.vocalFxSettings) setVocalFxSettings({ ...DEFAULT_VOCAL_FX_SETTINGS, ...snapshot.vocalFxSettings });
    if (Array.isArray(snapshot.timeMarkers)) setTimeMarkers(snapshot.timeMarkers);
  };

  const resetStudioSession = (title = 'Untitled Session') => {
    stopAllTrackPlayback();
    setCurrentSessionId(null);
    loadedSessionIdRef.current = null;
    setSessionTitle(title);
    setBeatInfo(null);
    setIsBeatPlaying(false);
    setCurrentTime(0);
    setSeekTime(0);
    setTakes({});
    setRecordedWaveforms({});
    setAuditionChannels({});
    setVerse1Channels(makeChannels());
    setVerse2Channels(makeChannels());
    setVerse3Channels(makeChannels());
    setHookChannels(makeChannels());
    setChannelFaderVolumes({ beat: 0.8, verse1: 1.0, verse2: 1.0, verse3: 1.0, hook: 1.0 });
    channelFaderVolumesRef.current = { beat: 0.8, verse1: 1.0, verse2: 1.0, verse3: 1.0, hook: 1.0 };
    setArmedChannel(null);
    setUndoStack([]);
    setRedoStack([]);
    setRecoverySnapshot(null);
    setCloudExportJobs([]);
    setShareLinks([]);
    setFeedbackComments([]);
    setTimeMarkers(DEFAULT_MARKERS);
    setVocalFxSettings(DEFAULT_VOCAL_FX_SETTINGS);
    setSessionNotes('');
    setSessionLyrics('');
    setSaveNotice(null);
    clearRecoverySnapshot().catch((error) => console.error('Recovery clear failed:', error));
  };

  useEffect(() => {
    const nextSessionName = location?.state?.sessionName?.trim();
    if (nextSessionName) resetStudioSession(nextSessionName);
  }, [location?.key]);

  const pushUndoSnapshot = () => {
    setUndoStack((current) => [...current, createStudioSnapshot()].slice(-50));
    setRedoStack([]);
  };

  const handleUndo = () => {
    const previous = undoStack?.[undoStack.length - 1];
    if (!previous) return;
    setRedoStack((current) => [...current, createStudioSnapshot()].slice(-50));
    setUndoStack((current) => current.slice(0, -1));
    applyStudioSnapshot(previous);
  };

  const handleRedo = () => {
    const next = redoStack?.[redoStack.length - 1];
    if (!next) return;
    setUndoStack((current) => [...current, createStudioSnapshot()].slice(-50));
    setRedoStack((current) => current.slice(0, -1));
    applyStudioSnapshot(next);
  };

  const tabs = [
    { id: 'verse1', label: 'Verse 1', icon: 'Music2' },
    { id: 'verse2', label: 'Verse 2', icon: 'Music3' },
    { id: 'verse3', label: 'Verse 3', icon: 'Music4' },
    { id: 'hook', label: 'Hook', icon: 'Sparkles' },
  ];

  const rightPanelTabs = [
    { id: 'controls', label: 'Controls', icon: 'Settings' },
    { id: 'mixer', label: 'Mixer', icon: 'SlidersHorizontal' },
    { id: 'export', label: 'Export', icon: 'Download' },
    { id: 'notes', label: 'Notes', icon: 'FileText' },
  ];

  const [subscriptionChecked, setSubscriptionChecked] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);

  const serializeBeatInfo = stripAudioBuffer;

  const createSerializableSessionData = (nextTakes = takes, nextBeatInfo = beatInfo) => ({
    ...autoSaveData,
    beatInfo: serializeBeatInfo(nextBeatInfo),
    takes: nextTakes,
  });

  const getRemoteTakeBlob = async (take, channelKey, takeAssets = {}, audioFiles = []) => {
    const asset = takeAssets?.[channelKey]?.[take?.id];
    if (asset?.blob) return asset.blob;
    if (asset?.dataUrl) return dataUrlToBlob(asset.dataUrl);

    const { sectionId, channelId } = parseChannelKey(channelKey);
    const matchingFile = audioFiles?.find((file) =>
      (take?.id && file?.take_id === take.id) ||
      (file?.section_id === sectionId && file?.channel_id === channelId && file?.file_role === 'take')
    );
    const remoteUrl = resolveAssetUrl(
      take?.remoteUrl ||
      take?.public_url ||
      take?.remotePath ||
      matchingFile?.public_url ||
      matchingFile?.file_path
    );
    if (!remoteUrl) return null;

    const response = await fetch(remoteUrl);
    if (!response.ok) throw new Error(`Could not load audio asset for ${take?.name || take?.id || channelKey}`);
    return response.blob();
  };

  const hydrateSessionPayload = async (payload, sourceTitle = sessionTitle) => {
    const sessionData = parseSessionData(payload?.sessionData || payload?.session_data || payload);
    const takeAssets = payload?.assets?.takes || {};
    const beatAsset = payload?.assets?.beat || null;
    const audioFiles = payload?.audio_files || payload?.audioFiles || [];
    const nextTakes = cloneHistoryValue(sessionData?.takes || {});
    const nextWaveforms = cloneHistoryValue(sessionData?.recordedWaveforms || {});

    stopAllTrackPlayback();
    audioBuffersRef.current = {};
    const context = audioContextRef.current || new (window.AudioContext || window.webkitAudioContext)();
    audioContextRef.current = context;

    await Promise.all(Object.entries(nextTakes).map(async ([channelKey, takesArr]) => {
      await Promise.all((takesArr || []).map(async (take) => {
        try {
          const blob = await getRemoteTakeBlob(take, channelKey, takeAssets, audioFiles);
          if (!blob) return;

          const blobUrl = URL.createObjectURL(blob);
          take.blobUrl = blobUrl;
          take.filePath = blobUrl;
          take.mimeType = blob.type || take?.mimeType || 'audio/webm';

          const buffer = await context.decodeAudioData(await blob.arrayBuffer());
          // Auto-key detection (fire & forget, non-blocking, attaches to outer scope when done)
          (async () => {
            try {
              const detected = await detectKeyFromAudioBuffer(buffer, { maxDurationSeconds: 45 });
              if (detected && detected.confidence > 0.3) {
                const label = `${detected.key} ${detected.mode}`;
                const scaleNotes = scaleNotesForKey(detected.key, detected.mode);
                window.dispatchEvent(new CustomEvent('makingitmix:key-detected', { detail: { label, scaleNotes, detected } }));
              }
            } catch (e) { console.warn('Key detect skipped:', e?.message); }
          })();
          audioBuffersRef.current[`${channelKey}:${take.id}`] = buffer;
          take.duration = buffer.duration;
          take.waveform = generateWaveformFromBuffer(buffer);
          if (take?.isSelected) {
            audioBuffersRef.current[channelKey] = buffer;
            nextWaveforms[channelKey] = take.waveform;
          }
        } catch (decodeError) {
          console.error('Failed to restore take audio:', decodeError);
          const remoteUrl = resolveAssetUrl(take?.remoteUrl || take?.remotePath);
          if (remoteUrl) {
            take.blobUrl = remoteUrl;
            take.filePath = remoteUrl;
          }
        }
      }));
    }));

    const syncChannels = (sectionId, channels = []) => channels.map((channel) => {
      const channelKey = `${sectionId}-${channel.id}`;
      const channelTakes = nextTakes?.[channelKey] || [];
      const selectedTake = channelTakes.find((take) => take?.isSelected) || channelTakes[channelTakes.length - 1];
      if (!selectedTake) return channel;
      return {
        ...channel,
        recorded: Boolean(selectedTake?.blobUrl || selectedTake?.remoteUrl || selectedTake?.remotePath),
        isPlaying: false,
        isRecording: false,
        filePath: selectedTake?.blobUrl || resolveAssetUrl(selectedTake?.remoteUrl || selectedTake?.remotePath) || channel?.filePath || null,
        duration: selectedTake?.duration ?? channel?.duration ?? null,
        selectedTakeId: selectedTake?.id ?? channel?.selectedTakeId ?? null,
        clipStart: selectedTake?.clipStart ?? channel?.clipStart ?? 0,
        clipEnd: selectedTake?.clipEnd ?? selectedTake?.duration ?? channel?.clipEnd ?? null,
        clipOffset: selectedTake?.clipOffset ?? channel?.clipOffset ?? 0,
      };
    });

    if (sessionData?.verse1) setVerse1Channels(syncChannels('verse1', sessionData.verse1));
    if (sessionData?.verse2) setVerse2Channels(syncChannels('verse2', sessionData.verse2));
    if (sessionData?.verse3) setVerse3Channels(syncChannels('verse3', sessionData.verse3));
    if (sessionData?.hook) setHookChannels(syncChannels('hook', sessionData.hook));
    if (sessionData?.beatInfo) {
      const beatFile = audioFiles?.find((file) => file?.file_role === 'beat');
      let nextBeatInfo = serializeBeatInfo(sessionData.beatInfo);
      if (beatAsset?.blob || beatAsset?.dataUrl) {
        const blob = beatAsset?.blob || dataUrlToBlob(beatAsset.dataUrl);
        nextBeatInfo = {
          ...nextBeatInfo,
          blobUrl: URL.createObjectURL(blob),
          mimeType: blob.type || nextBeatInfo?.mimeType,
        };
      } else {
        const remoteUrl = resolveAssetUrl(
          nextBeatInfo?.remoteUrl ||
          nextBeatInfo?.publicUrl ||
          nextBeatInfo?.remotePath ||
          beatFile?.public_url ||
          beatFile?.file_path
        );
        if (remoteUrl) {
          nextBeatInfo = {
            ...nextBeatInfo,
            remoteUrl,
            remotePath: beatFile?.file_path || nextBeatInfo?.remotePath,
            backendFileId: beatFile?.id || nextBeatInfo?.backendFileId,
          };
        }
      }
      setBeatInfo(nextBeatInfo);
    }
    setTakes(nextTakes);
    setRecordedWaveforms(nextWaveforms);
    setSelectedPreset(sessionData?.selectedPreset || selectedPreset);
    setAutotuneEnabled(Boolean(sessionData?.autotuneEnabled));
    if (sessionData?.retuneSpeed != null) setRetuneSpeed(sessionData.retuneSpeed);
    if (sessionData?.autotuneIntensity != null) setAutotuneIntensity(sessionData.autotuneIntensity);
    if (sessionData?.autotuneWetMix != null) setAutotuneWetMix(sessionData.autotuneWetMix);
    if (sessionData?.vocalFxSettings) setVocalFxSettings({ ...DEFAULT_VOCAL_FX_SETTINGS, ...sessionData.vocalFxSettings });
    if (sessionData?.loopStartPoint != null) setLoopStartPoint(sessionData.loopStartPoint);
    if (sessionData?.loopEndPoint != null) setLoopEndPoint(sessionData.loopEndPoint);
    setPunchInPoint(sessionData?.punchInPoint ?? null);
    setPunchOutPoint(sessionData?.punchOutPoint ?? null);
    setIsPunchInEnabled(Boolean(sessionData?.isPunchInEnabled));
    setIsLoopEnabled(Boolean(sessionData?.isLoopEnabled));
    if (sessionData?.countInEnabled != null) setCountInEnabled(Boolean(sessionData.countInEnabled));
    if (sessionData?.countInBars != null) setCountInBars(sessionData.countInBars);
    if (sessionData?.waveformZoom != null) setWaveformZoom(sessionData.waveformZoom);
    if (sessionData?.snapEnabled != null) setSnapEnabled(Boolean(sessionData.snapEnabled));
    if (sessionData?.gridSize != null) setGridSize(sessionData.gridSize);
    if (Array.isArray(sessionData?.timeMarkers)) setTimeMarkers(sessionData.timeMarkers);
    setSessionNotes(sessionData?.sessionNotes || '');
    setSessionLyrics(sessionData?.sessionLyrics || '');
    setSessionTitle(payload?.sessionTitle || payload?.title || sessionData?.sessionTitle || sourceTitle || 'Untitled Session');
    if (payload?.id || payload?.sessionId) setCurrentSessionId(payload?.id || payload?.sessionId);
    setUndoStack([]);
    setRedoStack([]);
  };

  const loadBackendSession = async (sessionId) => {
    const token = getBackendAuthToken();
    if (!token || !sessionId) return false;

    const response = await fetch(`${BACKEND_API_URL}/sessions/${sessionId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || 'Could not load cloud session');
    }

    const { data } = await response.json();
    await hydrateSessionPayload({
      ...data,
      sessionData: data?.session_data,
      sessionTitle: data?.title,
      audio_files: data?.audio_files || [],
    }, data?.title);
    return true;
  };

  const loadCloudSessions = async () => {
    const token = getBackendAuthToken();
    if (!token) {
      setCloudSessionError('Sign in with the backend account system to browse cloud projects.');
      return [];
    }

    setCloudSessionsLoading(true);
    setCloudSessionError('');
    try {
      const response = await fetch(`${BACKEND_API_URL}/sessions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Cloud projects could not be loaded.');
      }
      const payload = await response.json();
      const sessions = payload?.data || [];
      setCloudSessions(sessions);
      return sessions;
    } catch (error) {
      setCloudSessionError(error?.message || 'Cloud projects could not be loaded.');
      return [];
    } finally {
      setCloudSessionsLoading(false);
    }
  };

  const handleOpenCloudSession = async (sessionId) => {
    if (!sessionId) return;
    setLoadingSession(true);
    setCloudSessionError('');
    try {
      await loadBackendSession(sessionId);
      setShowCloudSessionBrowser(false);
      setSaveNotice({ type: 'success', message: 'Cloud project loaded.' });
    } catch (error) {
      setCloudSessionError(error?.message || 'Cloud project could not be loaded.');
    } finally {
      setLoadingSession(false);
    }
  };

  const uploadBeatAssetToBackend = async (sessionId) => {
    const token = getBackendAuthToken();
    if (!token || !sessionId || !beatInfo?.blobUrl || beatInfo?.backendFileId) return beatInfo;

    const response = await fetch(beatInfo.blobUrl);
    if (!response.ok) throw new Error(`Could not read beat ${beatInfo?.name || 'audio'}`);
    const blob = await response.blob();
    const extension = blob.type?.includes('wav') ? 'wav'
      : blob.type?.includes('mpeg') ? 'mp3'
        : blob.type?.includes('mp4') ? 'm4a'
          : 'webm';
    const formData = new FormData();
    formData.append('file', blob, `${beatInfo?.name || 'beat'}.${extension}`);
    formData.append('sessionId', sessionId);
    formData.append('fileRole', 'beat');
    formData.append('duration', String(beatInfo?.durationInSeconds || 0));
    formData.append('waveformData', JSON.stringify(beatInfo?.waveform || []));
    formData.append('metadata', JSON.stringify({
      name: beatInfo?.name || '',
      bpm: beatInfo?.bpm || null,
      key: beatInfo?.key || null,
      duration: beatInfo?.duration || null,
      durationInSeconds: beatInfo?.durationInSeconds || null,
      mimeType: beatInfo?.mimeType || blob.type || null,
    }));

    const uploadResponse = await fetch(`${BACKEND_API_URL}/upload/audio`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!uploadResponse.ok) {
      const message = await uploadResponse.text();
      throw new Error(message || `Upload failed for ${beatInfo?.name || 'beat'}`);
    }

    const uploaded = await uploadResponse.json();
    return {
      ...beatInfo,
      backendFileId: uploaded?.data?.id,
      remoteUrl: uploaded?.data?.public_url || uploaded?.url,
      remotePath: uploaded?.data?.file_path || uploaded?.path,
    };
  };

  const uploadTakeAssetToBackend = async (sessionId, channelKey, take) => {
    const token = getBackendAuthToken();
    if (!token || !sessionId || !take?.blobUrl || take?.backendFileId) return null;

    const { sectionId, channelId } = parseChannelKey(channelKey);
    const response = await fetch(take.blobUrl);
    if (!response.ok) throw new Error(`Could not read take ${take?.name || take?.id}`);
    const blob = await response.blob();
    const extension = blob.type?.includes('wav') ? 'wav'
      : blob.type?.includes('mpeg') ? 'mp3'
        : blob.type?.includes('mp4') ? 'm4a'
          : 'webm';
    const formData = new FormData();
    formData.append('file', blob, `${take?.name || take?.id || 'take'}.${extension}`);
    formData.append('sessionId', sessionId);
    formData.append('fileRole', 'take');
    formData.append('sectionId', sectionId || '');
    formData.append('channelId', channelId || '');
    formData.append('takeId', take?.id || '');
    formData.append('duration', String(take?.duration || 0));
    formData.append('waveformData', JSON.stringify(take?.waveform || []));
    formData.append('metadata', JSON.stringify({
      name: take?.name || '',
      createdAt: take?.createdAt || null,
      clipStart: take?.clipStart ?? 0,
      clipEnd: take?.clipEnd ?? take?.duration ?? null,
      clipOffset: take?.clipOffset ?? 0,
    }));

    const uploadResponse = await fetch(`${BACKEND_API_URL}/upload/audio`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!uploadResponse.ok) {
      const message = await uploadResponse.text();
      throw new Error(message || `Upload failed for ${take?.name || take?.id}`);
    }

    return uploadResponse.json();
  };

  const uploadSessionTakesToBackend = async (sessionId) => {
    const nextTakes = cloneHistoryValue(takes || {});
    const uploadTasks = [];

    Object.entries(nextTakes).forEach(([channelKey, takesArr]) => {
      (takesArr || []).forEach((take, takeIndex) => {
        uploadTasks.push((async () => {
          const uploaded = await uploadTakeAssetToBackend(sessionId, channelKey, take);
          if (!uploaded?.data) return;
          nextTakes[channelKey][takeIndex] = {
            ...nextTakes[channelKey][takeIndex],
            backendFileId: uploaded.data?.id,
            remoteUrl: uploaded.data?.public_url || uploaded.url,
            remotePath: uploaded.data?.file_path || uploaded.path,
          };
        })());
      });
    });

    await Promise.all(uploadTasks);
    return nextTakes;
  };

  const saveSessionToBackend = async () => {
    const token = getBackendAuthToken();
    if (!token) return null;

    const saveResponse = await fetch(`${BACKEND_API_URL}/sessions/save`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        id: currentSessionId,
        title: sessionTitle,
        preset: selectedPreset,
        autotune_enabled: autotuneEnabled,
        retune_speed: retuneSpeed,
        session_data: createSerializableSessionData(),
      }),
    });

    if (!saveResponse.ok) {
      const message = await saveResponse.text();
      throw new Error(message || 'Backend save failed');
    }

    const saved = await saveResponse.json();
    const sessionId = saved?.data?.id;
    if (!sessionId) throw new Error('Backend did not return a session id');
    setCurrentSessionId(sessionId);

    const nextBeatInfo = await uploadBeatAssetToBackend(sessionId);
    setBeatInfo(nextBeatInfo);
    const nextTakes = await uploadSessionTakesToBackend(sessionId);
    setTakes(nextTakes);

    const finalSaveResponse = await fetch(`${BACKEND_API_URL}/sessions/save`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        id: sessionId,
        title: sessionTitle,
        preset: selectedPreset,
        autotune_enabled: autotuneEnabled,
        retune_speed: retuneSpeed,
        session_data: createSerializableSessionData(nextTakes, nextBeatInfo),
      }),
    });

    if (!finalSaveResponse.ok) {
      const message = await finalSaveResponse.text();
      throw new Error(message || 'Backend session metadata update failed');
    }

    return finalSaveResponse.json();
  };
  
  const handleImportFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const payload = JSON.parse(event?.target?.result);
          await hydrateSessionPayload(payload, file.name?.replace(/\.mims$/i, '') || sessionTitle);
        } catch (error) {
          console.error('Failed to import session:', error);
          setRecordingError('Session import failed. The file may be invalid or missing audio assets.');
        }
      };
      reader?.readAsText(file);
    }
  };

  const handleImportSession = () => {
    if (getBackendAuthToken()) {
      setShowCloudSessionBrowser(true);
      loadCloudSessions();
      return;
    }
    sessionImportRef?.current?.click();
  };

  const handleExportSession = async () => {
    let beatAsset = null;
    const beatAssetUrl = beatInfo?.blobUrl || beatInfo?.remoteUrl || beatInfo?.publicUrl;
    if (beatAssetUrl) {
      try {
        const response = await fetch(beatAssetUrl);
        const blob = await response.blob();
        beatAsset = {
          mimeType: blob.type || beatInfo?.mimeType || 'audio/mpeg',
          dataUrl: await blobToDataUrl(blob),
        };
      } catch (error) {
        console.error('Failed to package beat audio:', error);
      }
    }

    const takeAssets = {};
    await Promise.all(Object.entries(takes || {}).map(async ([channelKey, takesArr]) => {
      takeAssets[channelKey] = {};
      await Promise.all((takesArr || []).map(async (take) => {
        if (!take?.blobUrl) return;
        try {
          const response = await fetch(take.blobUrl);
          const blob = await response.blob();
          takeAssets[channelKey][take.id] = {
            mimeType: blob.type || 'audio/webm',
            dataUrl: await blobToDataUrl(blob),
          };
        } catch (error) {
          console.error('Failed to package take audio:', error);
        }
      }));
    }));

    const sessionData = JSON.stringify({
      version: 2,
      kind: 'makingitmix-recording-session',
      sessionTitle,
      savedAt: new Date().toISOString(),
      sessionData: {
        ...autoSaveData,
        beatInfo: serializeBeatInfo(beatInfo),
      },
      assets: {
        beat: beatAsset,
        takes: takeAssets,
      },
    }, null, 2);
    const blob = new Blob([sessionData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${sessionTitle}.mims`;
    a?.click();
    URL.revokeObjectURL(url);
  };

  const buildRecoverySnapshot = async () => {
    let beatAsset = null;
    const beatAssetUrl = beatInfo?.blobUrl || beatInfo?.remoteUrl || beatInfo?.publicUrl;
    if (beatAssetUrl) {
      try {
        const response = await fetch(beatAssetUrl);
        if (response.ok) {
          const blob = await response.blob();
          beatAsset = {
            mimeType: blob.type || beatInfo?.mimeType || 'audio/mpeg',
            blob,
          };
        }
      } catch (error) {
        console.error('Failed to package recovery beat:', error);
      }
    }

    const takeAssets = {};
    await Promise.all(Object.entries(takes || {}).map(async ([channelKey, takesArr]) => {
      takeAssets[channelKey] = {};
      await Promise.all((takesArr || []).map(async (take) => {
        const takeUrl = take?.blobUrl || take?.filePath;
        if (!takeUrl) return;
        try {
          const response = await fetch(takeUrl);
          if (!response.ok) return;
          const blob = await response.blob();
          takeAssets[channelKey][take.id] = {
            mimeType: blob.type || take?.mimeType || 'audio/webm',
            blob,
          };
        } catch (error) {
          console.error('Failed to package recovery take:', error);
        }
      }));
    }));

    return {
      version: 2,
      kind: 'makingitmix-recording-recovery',
      sessionTitle,
      sessionId: currentSessionId,
      savedAt: new Date().toISOString(),
      sessionData: {
        ...autoSaveData,
        beatInfo: serializeBeatInfo(beatInfo),
      },
      assets: {
        beat: beatAsset,
        takes: takeAssets,
      },
    };
  };

  const getVersionSessionKey = () => currentSessionId || `local:${sessionTitle || 'Untitled Session'}`;

  const refreshSessionVersions = async ({ silent = false } = {}) => {
    if (!silent) setVersionsLoading(true);
    try {
      const allVersions = await loadProjectVersions();
      const key = getVersionSessionKey();
      const versions = (allVersions || [])
        .filter((version) => version?.sessionKey === key)
        .sort((a, b) => new Date(b?.createdAt || b?.savedAt || 0) - new Date(a?.createdAt || a?.savedAt || 0));
      setSessionVersions(versions);
      return versions;
    } catch (error) {
      console.error('Project history load failed:', error);
      if (!silent) {
        setSaveNotice({ type: 'error', message: 'Project history could not be loaded.' });
      }
      return [];
    } finally {
      if (!silent) setVersionsLoading(false);
    }
  };

  const handleOpenVersionHistory = async () => {
    setShowVersionHistory(true);
    await refreshSessionVersions();
  };

  const handleCreateProjectVersion = async () => {
    setVersionBusyId('create');
    setSaveNotice(null);
    try {
      const snapshot = await buildRecoverySnapshot();
      const createdAt = new Date().toISOString();
      const version = {
        ...snapshot,
        id: makeTakeId(),
        kind: 'makingitmix-recording-version',
        sessionKey: getVersionSessionKey(),
        label: `${sessionTitle || 'Untitled Session'} snapshot`,
        createdAt,
        savedAt: createdAt,
      };
      await saveProjectVersion(version);
      await refreshSessionVersions({ silent: true });
      setSaveNotice({ type: 'success', message: 'Project snapshot saved to history.' });
    } catch (error) {
      console.error('Project history save failed:', error);
      setSaveNotice({ type: 'error', message: 'Project snapshot could not be saved.' });
    } finally {
      setVersionBusyId('');
    }
  };

  const handleRestoreProjectVersion = async (version) => {
    if (!version) return;
    setVersionBusyId(version.id);
    try {
      await hydrateSessionPayload(version, version?.sessionTitle || sessionTitle);
      setShowVersionHistory(false);
      setSaveNotice({
        type: 'success',
        message: `Restored snapshot from ${new Date(version?.createdAt || version?.savedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.`,
      });
    } catch (error) {
      console.error('Project history restore failed:', error);
      setSaveNotice({ type: 'error', message: 'Project snapshot could not be restored.' });
    } finally {
      setVersionBusyId('');
    }
  };

  const handleDeleteProjectVersion = async (version) => {
    if (!version?.id) return;
    setVersionBusyId(version.id);
    try {
      await deleteProjectVersion(version.id);
      await refreshSessionVersions({ silent: true });
      setSaveNotice({ type: 'success', message: 'Project snapshot deleted.' });
    } catch (error) {
      console.error('Project history delete failed:', error);
      setSaveNotice({ type: 'error', message: 'Project snapshot could not be deleted.' });
    } finally {
      setVersionBusyId('');
    }
  };

  const restoreRecoverySnapshot = async () => {
    if (!recoverySnapshot) return;
    setRecoveryBusy(true);
    try {
      await hydrateSessionPayload(recoverySnapshot, recoverySnapshot?.sessionTitle || 'Recovered Session');
      setSaveNotice({ type: 'success', message: 'Recovered your latest local studio autosave.' });
      setRecoverySnapshot(null);
    } catch (error) {
      console.error('Recovery restore failed:', error);
      setSaveNotice({ type: 'error', message: 'Recovery could not be restored. The snapshot may be damaged.' });
    } finally {
      setRecoveryBusy(false);
    }
  };

  const discardRecoverySnapshot = async () => {
    setRecoveryBusy(true);
    try {
      await clearRecoverySnapshot();
      setRecoverySnapshot(null);
      setSaveNotice({ type: 'success', message: 'Local recovery snapshot discarded.' });
    } catch (error) {
      console.error('Recovery discard failed:', error);
      setSaveNotice({ type: 'error', message: 'Could not discard the local recovery snapshot.' });
    } finally {
      setRecoveryBusy(false);
    }
  };

  const handleSaveSession = () => {
    setSaveNotice(null);
    setShowSaveDialog(true);
  };

  const confirmSaveSession = async () => {
    setSaving(true);
    setSaveNotice(null);
    try {
      if (getBackendAuthToken()) {
        const saved = await saveSessionToBackend();
        setShowSaveDialog(false);
        await clearRecoverySnapshot().catch((error) => console.error('Recovery clear failed:', error));
        setRecoverySnapshot(null);
        setSaveNotice({
          type: 'success',
          message: `Cloud saved${saved?.data?.updated_at ? ` at ${new Date(saved.data.updated_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : ''}.`,
        });
        return;
      }

      const sessionPayload = createSerializableSessionData();
      if (currentSessionId) {
        await supabase?.from('audio_sessions')?.update({
          title: sessionTitle,
          session_data: sessionPayload,
          updated_at: new Date()?.toISOString(),
        })?.eq('id', currentSessionId);
      } else {
        const { data } = await supabase?.from('audio_sessions')?.insert({
          user_id: user?.id,
          title: sessionTitle,
          session_data: sessionPayload,
        })?.select()?.single();
        if (data) setCurrentSessionId(data?.id);
      }
      setShowSaveDialog(false);
      await clearRecoverySnapshot().catch((error) => console.error('Recovery clear failed:', error));
      setRecoverySnapshot(null);
      setSaveNotice({
        type: 'success',
        message: 'Session saved.',
      });
    } catch (error) {
      console.error('Save error:', error);
      setSaveNotice({
        type: 'error',
        message: error?.message || 'Save failed. Check your connection and try again.',
      });
      setRecordingError(error?.message || 'Save failed. Check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleShareSession = async () => {
    setShareSaving(true);
    setSaveNotice(null);
    try {
      const token = getBackendAuthToken();
      if (!token) {
        throw new Error('Sign in with the backend account system before creating share links.');
      }

      let sessionId = currentSessionId;
      if (!sessionId) {
        const saved = await saveSessionToBackend();
        sessionId = saved?.data?.id;
      }
      if (!sessionId) throw new Error('Cloud save the session before sharing.');

      const response = await fetch(`${BACKEND_API_URL}/sessions/${sessionId}/share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ expires_in_days: 30, permission: 'comment' }),
      });
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Share link could not be created.');
      }

      const payload = await response.json();
      const shareUrl = payload?.data?.share_url || `${BACKEND_PUBLIC_URL}/share/${payload?.data?.token}`;
      if (!shareUrl) throw new Error('Backend did not return a share link.');

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        setSaveNotice({ type: 'success', message: `Feedback share link copied: ${shareUrl}` });
      } else {
        setSaveNotice({ type: 'success', message: `Feedback share link ready: ${shareUrl}` });
      }
      setShareLinks((links) => [payload?.data, ...links.filter((link) => link?.id !== payload?.data?.id)]);
      loadShareLinks(sessionId, { silent: true });
    } catch (error) {
      console.error('Share error:', error);
      setSaveNotice({
        type: 'error',
        message: error?.message || 'Share link could not be created.',
      });
    } finally {
      setShareSaving(false);
    }
  };

  const loadShareLinks = async (sessionId = currentSessionId, { silent = false } = {}) => {
    const token = getBackendAuthToken();
    if (!token || !sessionId) return [];
    if (!silent) setSharesLoading(true);
    try {
      const response = await fetch(`${BACKEND_API_URL}/sessions/${sessionId}/shares`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Share links could not be loaded.');
      }
      const payload = await response.json();
      const links = payload?.data || [];
      setShareLinks(links);
      return links;
    } catch (error) {
      console.error('Share links load error:', error);
      if (!silent) {
        setSaveNotice({
          type: 'error',
          message: error?.message || 'Share links could not be loaded.',
        });
      }
      return [];
    } finally {
      if (!silent) setSharesLoading(false);
    }
  };

  const handleCopyShareLink = async (link) => {
    if (!link?.share_url) return;
    try {
      await navigator.clipboard?.writeText?.(link.share_url);
      setSaveNotice({ type: 'success', message: `Feedback link copied: ${link.share_url}` });
    } catch (error) {
      setSaveNotice({ type: 'success', message: `Feedback link ready: ${link.share_url}` });
    }
  };

  const loadFeedbackComments = async (sessionId = currentSessionId, { silent = false } = {}) => {
    const token = getBackendAuthToken();
    if (!token || !sessionId) return [];
    if (!silent) setFeedbackLoading(true);
    try {
      const response = await fetch(`${BACKEND_API_URL}/sessions/${sessionId}/comments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Feedback comments could not be loaded.');
      }
      const payload = await response.json();
      const comments = payload?.data || [];
      setFeedbackComments(comments);
      return comments;
    } catch (error) {
      console.error('Feedback comments load error:', error);
      if (!silent) {
        setSaveNotice({
          type: 'error',
          message: error?.message || 'Feedback comments could not be loaded.',
        });
      }
      return [];
    } finally {
      if (!silent) setFeedbackLoading(false);
    }
  };

  const handleJumpToFeedbackTimestamp = (seconds) => {
    const nextTime = Math.max(0, Number(seconds) || 0);
    setCurrentTime(nextTime);
    setSeekTime(nextTime);
    beatImporterRef?.current?.seekTo?.(nextTime);
  };

  const handleSeekToMarker = (seconds) => {
    const nextTime = clampTimelineTime(seconds);
    setCurrentTime(nextTime);
    setSeekTime(nextTime);
    beatImporterRef?.current?.seekTo?.(nextTime);
  };

  const handleTimeMarkersChange = (nextMarkers) => {
    pushUndoSnapshot();
    setTimeMarkers(Array.isArray(nextMarkers) ? nextMarkers : DEFAULT_MARKERS);
  };

  const handleRevokeShareLink = async (link) => {
    const token = getBackendAuthToken();
    if (!token || !currentSessionId || !link?.id) return;
    setShareRevokingId(link.id);
    try {
      const response = await fetch(`${BACKEND_API_URL}/sessions/${currentSessionId}/shares/${link.id}/revoke`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Share link could not be revoked.');
      }
      const payload = await response.json();
      setShareLinks((links) => links.map((item) => item?.id === link.id ? payload?.data : item));
      setSaveNotice({ type: 'success', message: 'Feedback link revoked.' });
    } catch (error) {
      setSaveNotice({
        type: 'error',
        message: error?.message || 'Share link could not be revoked.',
      });
    } finally {
      setShareRevokingId(null);
    }
  };

  const normalizeCloudExportJob = (job) => ({
    ...job,
    result_file: job?.result_file ? {
      ...job.result_file,
      download_url: resolveAssetUrl(job.result_file.public_url || job.result_file.file_path),
    } : null,
  });

  const loadCloudExportJobs = async (sessionId = currentSessionId, { silent = false } = {}) => {
    const token = getBackendAuthToken();
    if (!token || !sessionId) return [];
    if (!silent) setCloudExportsLoading(true);
    try {
      const response = await fetch(`${BACKEND_API_URL}/sessions/${sessionId}/export-jobs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Cloud export jobs could not be loaded.');
      }
      const payload = await response.json();
      const jobs = (payload?.data || []).map(normalizeCloudExportJob);
      setCloudExportJobs(jobs);
      return jobs;
    } catch (error) {
      console.error('Cloud export jobs load error:', error);
      if (!silent) {
        setSaveNotice({
          type: 'error',
          message: error?.message || 'Cloud export jobs could not be loaded.',
        });
      }
      return [];
    } finally {
      if (!silent) setCloudExportsLoading(false);
    }
  };

  const handleQueueCloudExport = async (exportRequest) => {
    setSaveNotice(null);
    try {
      const token = getBackendAuthToken();
      if (!token) {
        throw new Error('Sign in with the backend account system before queueing cloud exports.');
      }

      let sessionId = currentSessionId;
      if (!sessionId) {
        const saved = await saveSessionToBackend();
        sessionId = saved?.data?.id;
      } else {
        await saveSessionToBackend();
      }
      if (!sessionId) throw new Error('Cloud save the session before queueing export.');

      const response = await fetch(`${BACKEND_API_URL}/sessions/${sessionId}/export-jobs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(exportRequest),
      });
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Cloud export could not be queued.');
      }

      const payload = await response.json();
      const job = normalizeCloudExportJob(payload?.data);
      setCloudExportJobs((jobs) => [job, ...jobs.filter((existing) => existing?.id !== job?.id)]);
      setSaveNotice({
        type: 'success',
        message: `Cloud export queued${job?.id ? `: ${job.id.slice(0, 8)}` : ''}.`,
      });
      loadCloudExportJobs(sessionId, { silent: true });
      return job;
    } catch (error) {
      setSaveNotice({
        type: 'error',
        message: error?.message || 'Cloud export could not be queued.',
      });
      throw error;
    }
  };

  const handleCancelCloudExport = async (job) => {
    const token = getBackendAuthToken();
    if (!token || !job?.id) return null;
    setSaveNotice(null);
    try {
      const response = await fetch(`${BACKEND_API_URL}/export-jobs/${job.id}/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Cloud export could not be canceled.');
      }
      const payload = await response.json();
      const canceledJob = normalizeCloudExportJob(payload?.data);
      setCloudExportJobs((jobs) => jobs.map((existing) => existing?.id === canceledJob?.id ? canceledJob : existing));
      setSaveNotice({ type: 'success', message: 'Cloud export canceled.' });
      return canceledJob;
    } catch (error) {
      setSaveNotice({
        type: 'error',
        message: error?.message || 'Cloud export could not be canceled.',
      });
      throw error;
    }
  };

  const createMixHandoffData = () => {
    const sections = {
      verse1: verse1Channels,
      verse2: verse2Channels,
      verse3: verse3Channels,
      hook: hookChannels,
    };
    const userFineTuned = exportChannels.some((channel) =>
      Math.abs((channel?.volume ?? 1) - 1) > 0.01 ||
      Math.abs(channel?.pan ?? 0) > 0.01
    ) || Object.values(channelFaderVolumes || {}).some((volume) => Math.abs((volume ?? 1) - 1) > 0.01);

    return {
      sessionId: currentSessionId,
      sessionTitle,
      beatInfo: serializeBeatInfo(beatInfo),
      sections,
      takes,
      channelFaderVolumes,
      selectedPreset,
      autotuneEnabled,
      retuneSpeed,
      userFineTuned,
      source: 'recording-studio',
    };
  };

  const handleProceedToMix = () => {
    const sessionMixData = createMixHandoffData();
    navigate('/fine-tune-mix-page', {
      state: {
        sessionMixData,
        sessionData: createSerializableSessionData(),
      },
    });
  };

  const clampTimelineTime = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return 0;
    return Math.max(0, Math.min(getProjectDuration(), numeric));
  };

  const getLoopRange = () => {
    const start = clampTimelineTime(loopStartPoint);
    const fallbackEnd = Math.min(getProjectDuration(), Math.max(start + 1, 16));
    const end = Math.max(start + 0.25, clampTimelineTime(loopEndPoint || fallbackEnd));
    return { start, end };
  };

  const getPunchRange = () => {
    const start = punchInPoint == null ? currentTime : clampTimelineTime(punchInPoint);
    const end = punchOutPoint == null ? null : Math.max(start + 0.25, clampTimelineTime(punchOutPoint));
    return { start, end };
  };

  const formatTimelineLabel = (seconds) => {
    const safe = Math.max(0, Number(seconds) || 0);
    const mins = Math.floor(safe / 60);
    const secs = Math.floor(safe % 60);
    const tenths = Math.floor((safe % 1) * 10);
    return `${mins}:${secs.toString().padStart(2, '0')}.${tenths}`;
  };

  const getLatencyAdjustedClipOffset = (recordStartTime = 0) => {
    const start = Math.max(0, Number(recordStartTime) || 0);
    const offsetSeconds = (Number(manualLatencyMs) || 0) / 1000;
    return Math.max(0, start - offsetSeconds);
  };

  const updateLoopPoint = (key, value) => {
    const nextValue = clampTimelineTime(value);
    if (key === 'start') {
      setLoopStartPoint(Math.min(nextValue, Math.max(0, loopEndPoint - 0.25)));
    } else {
      setLoopEndPoint(Math.max(loopStartPoint + 0.25, nextValue));
    }
  };

  const updatePunchPoint = (key, value) => {
    const nextValue = clampTimelineTime(value);
    if (key === 'in') {
      setPunchInPoint(nextValue);
      if (punchOutPoint != null && punchOutPoint <= nextValue) {
        setPunchOutPoint(Math.min(getProjectDuration(), nextValue + 4));
      }
    } else {
      const start = punchInPoint ?? currentTime;
      setPunchOutPoint(Math.max(start + 0.25, nextValue));
    }
  };

  const handleToggleLoop = () => {
    setIsLoopEnabled((enabled) => {
      const nextEnabled = !enabled;
      if (nextEnabled && loopEndPoint <= loopStartPoint) {
        setLoopEndPoint(Math.min(getProjectDuration(), loopStartPoint + 16));
      }
      return nextEnabled;
    });
  };

  const handleTogglePunchIn = () => {
    setIsPunchInEnabled((enabled) => {
      const nextEnabled = !enabled;
      if (nextEnabled) {
        const start = punchInPoint ?? currentTime;
        setPunchInPoint(start);
        if (punchOutPoint == null || punchOutPoint <= start) {
          setPunchOutPoint(Math.min(getProjectDuration(), start + 8));
        }
      }
      return nextEnabled;
    });
  };

  const seekTimeline = (time, { restartPlayback = false } = {}) => {
    const nextTime = clampTimelineTime(time);
    setCurrentTime(nextTime);
    beatImporterRef?.current?.seekTo?.(nextTime);
    if (restartPlayback && isBeatPlaying) {
      playTimelineClips(nextTime);
      startPlayheadClock(nextTime);
    }
    return nextTime;
  };

  const clearPunchOutTimer = () => {
    if (punchOutTimeoutRef.current) {
      clearTimeout(punchOutTimeoutRef.current);
      punchOutTimeoutRef.current = null;
    }
  };

  const clearCountInTimers = () => {
    countInTimeoutsRef.current.forEach((timer) => clearTimeout(timer));
    countInTimeoutsRef.current = [];
    setCountInRemaining(null);
  };

  const stopMetronomeClock = () => {
    if (metronomeIntervalRef.current) {
      clearTimeout(metronomeIntervalRef.current);
      clearInterval(metronomeIntervalRef.current);
      metronomeIntervalRef.current = null;
    }
  };

  const playTransportClick = async (beatIndex = 0) => {
    const context = metronomeCtxRef.current || new (window.AudioContext || window.webkitAudioContext)();
    metronomeCtxRef.current = context;
    if (context.state === 'suspended') {
      await context.resume();
    }
    const beat = Math.max(0, Number(beatIndex) || 0) % 4;
    const osc = context.createOscillator();
    const gain = context.createGain();
    osc.type = 'square';
    osc.frequency.value = beat === 0 ? 1400 : 950;
    gain.gain.setValueAtTime(0.0001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(beat === 0 ? 0.18 : 0.1, context.currentTime + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.055);
    osc.connect(gain);
    gain.connect(context.destination);
    osc.start(context.currentTime);
    osc.stop(context.currentTime + 0.06);
  };

  const startMetronomeClock = (startAt = currentTime) => {
    stopMetronomeClock();
    if (!metronomeEnabled) return;

    const bpmValue = Number(beatInfo?.bpm) || 120;
    const beatSeconds = Math.max(0.12, 60 / bpmValue);
    const startSeconds = Math.max(0, Number(startAt) || 0);
    const startBeat = startSeconds / beatSeconds;
    const nextBeatIndex = Math.ceil(startBeat - 0.0001);
    const delayMs = Math.max(0, (nextBeatIndex * beatSeconds - startSeconds) * 1000);

    const fireClick = (beatIndex) => {
      metronomeBeatRef.current = beatIndex;
      playTransportClick(beatIndex);
      metronomeBeatRef.current += 1;
    };

    metronomeBeatRef.current = nextBeatIndex;
    metronomeIntervalRef.current = setTimeout(() => {
      fireClick(metronomeBeatRef.current);
      metronomeIntervalRef.current = setInterval(() => {
        fireClick(metronomeBeatRef.current);
      }, beatSeconds * 1000);
    }, delayMs);
  };

  const playCountInClick = async (beatIndex = 0) => {
    const context = metronomeCtxRef.current || new (window.AudioContext || window.webkitAudioContext)();
    metronomeCtxRef.current = context;
    if (context.state === 'suspended') {
      await context.resume();
    }
    const osc = context.createOscillator();
    const gain = context.createGain();
    const isAccent = beatIndex % 4 === 0;
    osc.type = 'square';
    osc.frequency.value = isAccent ? 1500 : 950;
    gain.gain.setValueAtTime(0.0001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(isAccent ? 0.22 : 0.12, context.currentTime + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.07);
    osc.connect(gain);
    gain.connect(context.destination);
    osc.start(context.currentTime);
    osc.stop(context.currentTime + 0.08);
  };

  const runCountIn = (onComplete) => {
    clearCountInTimers();
    const bpmValue = Number(beatInfo?.bpm) || 120;
    const beatMs = Math.max(120, (60 / bpmValue) * 1000);
    const totalBeats = Math.max(1, Number(countInBars) || 1) * 4;
    setCountInRemaining(totalBeats);

    for (let beat = 0; beat < totalBeats; beat += 1) {
      const timer = setTimeout(() => {
        playCountInClick(beat);
        setCountInRemaining(totalBeats - beat - 1);
      }, beat * beatMs);
      countInTimeoutsRef.current.push(timer);
    }

    const completeTimer = setTimeout(() => {
      clearCountInTimers();
      onComplete?.();
    }, totalBeats * beatMs);
    countInTimeoutsRef.current.push(completeTimer);
  };

  const handleTransportPlay = () => {
    if (isBeatPlaying) {
      setIsBeatPlaying(false);
      beatImporterRef?.current?.pause();
      stopAllTrackPlayback();
      stopMetronomeClock();
      stopPlayheadClock();
    } else {
      const { start, end } = getLoopRange();
      const playbackStart = isLoopEnabled && (currentTime < start || currentTime >= end)
        ? seekTimeline(start)
        : currentTime;
      setIsBeatPlaying(true);
      if (beatInfo) {
        beatImporterRef?.current?.seekTo?.(playbackStart);
        beatImporterRef?.current?.play();
      }
      playTimelineClips(playbackStart);
      startMetronomeClock(playbackStart);
      startPlayheadClock(playbackStart);
    }
  };

  const handleTransportStop = () => {
    clearCountInTimers();
    clearPunchOutTimer();
    if (isRecording && mediaRecorderRef?.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    stopAllTrackPlayback();
    stopMetronomeClock();
    setIsBeatPlaying(false);
    setIsRecording(false);
    beatImporterRef?.current?.pause();
    beatImporterRef?.current?.seekTo?.(0);
    stopPlayheadClock();
    setCurrentTime(0);
  };

  const updateSectionChannel = (sectionId, channelId, updater) => {
    const updateChannels = (channels) =>
      channels?.map((channel) =>
        channel?.id === channelId ? updater(channel) : channel
      );

    if (sectionId === 'verse1') setVerse1Channels(updateChannels);
    else if (sectionId === 'verse2') setVerse2Channels(updateChannels);
    else if (sectionId === 'verse3') setVerse3Channels(updateChannels);
    else if (sectionId === 'hook') setHookChannels(updateChannels);
  };

  const getSectionChannels = (sectionId) => {
    if (sectionId === 'verse1') return verse1Channels;
    if (sectionId === 'verse2') return verse2Channels;
    if (sectionId === 'verse3') return verse3Channels;
    if (sectionId === 'hook') return hookChannels;
    return [];
  };

  const getSectionChannel = (sectionId, channelId) =>
    getSectionChannels(sectionId)?.find((channel) => channel?.id === channelId);

  const hasSoloedRecordedChannel = () =>
    exportChannels?.some((channel) => channel?.recorded && channel?.soloed);

  const isChannelAudibleInMix = (channel) =>
    Boolean(channel?.recorded && !channel?.muted && (!hasSoloedRecordedChannel() || channel?.soloed));

  const getTimelineTakesForChannel = (channel) => {
    const channelKey = channel?.channelKey || `${channel?.sectionId}-${channel?.id}`;
    const channelTakes = takes?.[channelKey] || [];
    const activeRegions = channelTakes.filter((take) => take?.timelineActive === true && hasTakeAudioAsset(take));
    if (activeRegions.length) return activeRegions;
    if (channelTakes.some((take) => take?.timelineActive != null)) return [];

    const selectedTake = channelTakes.find((take) => take?.id === channel?.selectedTakeId)
      || channelTakes.find((take) => take?.isSelected)
      || null;
    return selectedTake && hasTakeAudioAsset(selectedTake) ? [selectedTake] : [];
  };

  const stopTrackPlayback = (channelKey) => {
    if (channelKey && !String(channelKey).includes('::')) {
      const relatedKeys = new Set([
        ...Object.keys(audioPlayersRef.current || {}),
        ...Object.keys(audioPlayerTimeoutsRef.current || {}),
      ].filter((key) => key?.startsWith(`${channelKey}::`)));
      relatedKeys.forEach((key) => stopTrackPlayback(key));
    }

    const baseChannelKey = String(channelKey || '').split('::')[0];
    const timers = audioPlayerTimeoutsRef.current?.[channelKey];
    if (timers) {
      const timerList = Array.isArray(timers) ? timers : [timers];
      timerList.forEach((timer) => clearTimeout(timer));
      delete audioPlayerTimeoutsRef.current[channelKey];
    }

    const player = audioPlayersRef.current?.[channelKey];
    if (player) {
      try {
        if (typeof player.stop === 'function') player.stop();
        else if (typeof player.pause === 'function') {
          player.pause();
          player.currentTime = 0;
        }
      } catch (error) {
        console.error('Track playback stop failed:', error);
      }
      delete audioPlayersRef.current[channelKey];
    }

    const { sectionId, channelId } = parseChannelKey(baseChannelKey);
    if (sectionId && channelId) {
      updateSectionChannel(sectionId, channelId, (channel) => ({
        ...channel,
        isPlaying: false,
      }));
    }
  };

  const stopAllTrackPlayback = () => {
    Object.keys(audioPlayersRef.current || {}).forEach(stopTrackPlayback);
    Object.keys(audioPlayerTimeoutsRef.current || {}).forEach((channelKey) => stopTrackPlayback(channelKey));
  };

  const getProjectDuration = () => {
    const recordedEnd = exportChannels?.reduce((max, channel) => {
      if (!channel?.recorded) return max;
      const timelineTakes = getTimelineTakesForChannel(channel);
      if (!timelineTakes.length) {
        const clipStart = Math.max(0, Number(channel?.clipStart) || 0);
        const clipEnd = Math.max(clipStart, Number(channel?.clipEnd ?? channel?.duration) || channel?.duration || clipStart);
        const clipOffset = Math.max(0, Number(channel?.clipOffset) || 0);
        return Math.max(max, clipOffset + Math.max(0, clipEnd - clipStart));
      }
      return timelineTakes.reduce((takeMax, take) => {
        const range = getTakeClipRange(take);
        return Math.max(takeMax, range.timelineEnd);
      }, max);
    }, 0);
    return Math.max(beatInfo?.durationInSeconds || 0, recordedEnd || 0, 180);
  };

  const startPlayheadClock = (startAt = currentTime) => {
    stopPlayheadClock();
    playStartTimeRef.current = startAt;
    playStartWallRef.current = Date.now();
    const projectDuration = getProjectDuration();
    const loopRange = getLoopRange();

    playheadIntervalRef.current = setInterval(() => {
      const elapsed = (Date.now() - playStartWallRef.current) / 1000;
      const rawNextTime = playStartTimeRef.current + elapsed;
      if (isLoopEnabled && rawNextTime >= loopRange.end) {
        const nextStart = loopRange.start;
        setCurrentTime(nextStart);
        beatImporterRef?.current?.seekTo?.(nextStart);
        if (beatInfo) beatImporterRef?.current?.play();
        playTimelineClips(nextStart);
        startMetronomeClock(nextStart);
        playStartTimeRef.current = nextStart;
        playStartWallRef.current = Date.now();
        return;
      }

      const nextTime = Math.min(projectDuration, rawNextTime);
      setCurrentTime(nextTime);
      if (nextTime >= projectDuration) {
        setIsBeatPlaying(false);
        stopAllTrackPlayback();
        stopPlayheadClock();
      }
    }, 60);
  };

  const stopPlayheadClock = () => {
    if (playheadIntervalRef.current) {
      clearInterval(playheadIntervalRef.current);
      playheadIntervalRef.current = null;
    }
  };

  const playTimelineClips = (startAt = currentTime) => {
    stopAllTrackPlayback();
    const timelineStart = Math.max(0, Number(startAt) || 0);

    exportChannels
      ?.filter(isChannelAudibleInMix)
      ?.forEach((channel) => {
        const channelKey = channel.channelKey;
        const timelineTakes = getTimelineTakesForChannel(channel);

        timelineTakes.forEach((take) => {
          const assetUrl = take?.blobUrl || take?.filePath || resolveAssetUrl(take?.remoteUrl || take?.remotePath) || channel?.filePath;
          if (!assetUrl) return;
          const { clipStart, clipEnd, clipOffset, timelineEnd } = getTakeClipRange(take);
          const clipLength = clipEnd - clipStart;

          if (clipLength <= 0 || timelineStart >= timelineEnd) return;

          const playerKey = `${channelKey}::${take.id}`;
          const startDelaySeconds = Math.max(0, clipOffset - timelineStart);
          const sourceOffset = clipStart + Math.max(0, timelineStart - clipOffset);
          const remainingDuration = Math.max(0.05, clipEnd - sourceOffset);

          const startPlayer = async () => {
            const context = audioContextRef.current || new (window.AudioContext || window.webkitAudioContext)();
            audioContextRef.current = context;
            if (context.state === 'suspended') await context.resume();

            let buffer = audioBuffersRef.current?.[`${channelKey}:${take.id}`] || audioBuffersRef.current?.[channelKey];
            if (!buffer) {
              const response = await fetch(assetUrl);
              if (!response.ok) throw new Error(`Could not load clip ${take?.name || take?.id}`);
              buffer = await context.decodeAudioData(await response.arrayBuffer());
              audioBuffersRef.current[`${channelKey}:${take.id}`] = buffer;
            }

            const bufferDuration = Number(buffer.duration) || 0;
            const boundedOffset = Math.max(0, Math.min(sourceOffset, bufferDuration));
            const boundedDuration = Math.min(remainingDuration, bufferDuration - boundedOffset);
            if (boundedOffset >= bufferDuration || boundedDuration <= 0.01) return;

            const source = context.createBufferSource();
            const gain = context.createGain();
            const panner = context.createStereoPanner ? context.createStereoPanner() : null;
            const volume = Math.max(0, Math.min(1, (channel?.volume ?? 1) * (channelFaderVolumesRef.current?.[channel.sectionId] ?? 1)));
            const pan = Math.max(-1, Math.min(1, Number(channel?.pan) || 0));

            source.buffer = buffer;
            gain.gain.value = volume;
            if (panner) {
              panner.pan.value = pan;
              source.connect(gain);
              gain.connect(panner);
              panner.connect(context.destination);
            } else {
              source.connect(gain);
              gain.connect(context.destination);
            }

            source.onended = () => stopTrackPlayback(playerKey);
            audioPlayersRef.current[playerKey] = {
              source,
              gain,
              panner,
              stop: () => {
                try {
                  source.onended = null;
                  source.stop();
                } catch (error) {
                  // Source may already be stopped by the audio clock.
                }
                try {
                  source.disconnect();
                  gain.disconnect();
                  panner?.disconnect();
                } catch (error) {
                  // Nodes can already be disconnected during cleanup.
                }
              },
            };
            updateSectionChannel(channel.sectionId, channel.id, (current) => ({
              ...current,
              isPlaying: true,
            }));

            source.start(0, boundedOffset, boundedDuration);
            const stopTimer = setTimeout(() => stopTrackPlayback(playerKey), boundedDuration * 1000);
            audioPlayerTimeoutsRef.current[playerKey] = [
              ...(audioPlayerTimeoutsRef.current[playerKey] || []),
              stopTimer,
            ];
          };

          if (startDelaySeconds > 0) {
            const startTimer = setTimeout(() => {
              startPlayer().catch((error) => {
                console.error('Timeline clip playback failed:', error);
                stopTrackPlayback(playerKey);
                setRecordingError('One of the timeline clips could not be played.');
              });
            }, startDelaySeconds * 1000);
            audioPlayerTimeoutsRef.current[playerKey] = [
              ...(audioPlayerTimeoutsRef.current[playerKey] || []),
              startTimer,
            ];
            updateSectionChannel(channel.sectionId, channel.id, (current) => ({
              ...current,
              isPlaying: true,
            }));
          } else {
            startPlayer().catch((error) => {
              console.error('Timeline clip playback failed:', error);
              stopTrackPlayback(playerKey);
              setRecordingError('One of the timeline clips could not be played.');
            });
          }
        });
      });
  };

  const finalizeRecording = async () => {
    clearPunchOutTimer();
    const target = armedChannel;
    if (!target) return;

    const blob = new Blob(recordedChunksRef.current, {
      type: recordingMimeTypeRef.current || 'audio/webm',
    });
    recordedChunksRef.current = [];

    if (!blob.size) {
      updateSectionChannel(target.sectionId, target.channelId, (channel) => ({
        ...channel,
        isRecording: false,
      }));
      setRecordingError('No audio was captured. Check your input device and try again.');
      return;
    }

    const blobUrl = URL.createObjectURL(blob);
    let duration = null;
    let waveform = [];
    let decodedBuffer = null;

    try {
      const context = audioContextRef.current || new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = context;
      decodedBuffer = await context.decodeAudioData(await blob.arrayBuffer());
      duration = decodedBuffer.duration;
      waveform = generateWaveformFromBuffer(decodedBuffer);
    } catch (error) {
      console.error('Recording decode failed:', error);
    }

    const channelKey = `${target.sectionId}-${target.channelId}`;
    const takeId = makeTakeId();
    if (decodedBuffer) {
      audioBuffersRef.current[channelKey] = decodedBuffer;
      audioBuffersRef.current[`${channelKey}:${takeId}`] = decodedBuffer;
    }
    const clipSettings = normalizeClipSettings({
      clipStart: 0,
      clipEnd: duration,
      clipOffset: getLatencyAdjustedClipOffset(recordingStartTimeRef.current),
    }, duration);
    setRecordedWaveforms((current) => ({
      ...current,
      [channelKey]: waveform,
    }));
    setTakes((current) => ({
      ...current,
      [channelKey]: [
        ...(current?.[channelKey] || []).map((take) => ({
          ...take,
          isSelected: false,
          timelineActive: false,
        })),
        {
          id: takeId,
          name: `Take ${(current?.[channelKey]?.length || 0) + 1}`,
          blobUrl,
          duration,
          createdAt: new Date().toISOString(),
          waveform,
          ...clipSettings,
          isSelected: true,
          timelineActive: true,
        },
      ],
    }));
    updateSectionChannel(target.sectionId, target.channelId, (channel) => ({
      ...channel,
      recorded: true,
      isRecording: false,
      duration,
      filePath: blobUrl,
      selectedTakeId: takeId,
      ...clipSettings,
    }));
  };

  const startRecordingCapture = async (punchRangeOverride = null) => {
    try {
      if (typeof MediaRecorder === 'undefined') {
        setRecordingError('This browser does not support audio recording. Use Chrome, Edge, or Safari.');
        return;
      }
      await initializeAudioContext();
      if (!mediaStreamRef.current) return;

      recordedChunksRef.current = [];
      recordingMimeTypeRef.current = getSupportedRecordingMimeType();
      const recorder = new MediaRecorder(
        mediaStreamRef.current,
        recordingMimeTypeRef.current ? { mimeType: recordingMimeTypeRef.current } : undefined
      );
      recorder.ondataavailable = (event) => {
        if (event?.data?.size > 0) recordedChunksRef.current.push(event.data);
      };
      recorder.onstop = finalizeRecording;
      mediaRecorderRef.current = recorder;
      recorder.start(250);

      const punchRange = punchRangeOverride || (isPunchInEnabled ? getPunchRange() : { start: currentTime, end: null });
      const recordStartTime = seekTimeline(punchRange.start);
      recordingStartTimeRef.current = recordStartTime;
      setRecordingError(null);
      setIsRecording(true);
      updateSectionChannel(armedChannel.sectionId, armedChannel.channelId, (channel) => ({
        ...channel,
        isRecording: true,
      }));
      if (beatInfo) {
        setIsBeatPlaying(true);
        beatImporterRef?.current?.seekTo?.(recordStartTime);
        beatImporterRef?.current?.play();
      }
      playTimelineClips(recordStartTime);
      startMetronomeClock(recordStartTime);
      startPlayheadClock(recordStartTime);
      if (punchRange.end != null) {
        punchOutTimeoutRef.current = setTimeout(() => {
          if (mediaRecorderRef?.current?.state === 'recording') {
            mediaRecorderRef.current.stop();
          }
          setIsRecording(false);
        }, Math.max(250, (punchRange.end - recordStartTime) * 1000));
      }
    } catch (error) {
      console.error('Recording start failed:', error);
      setRecordingError('Recording could not start. Check microphone permission and input device.');
    }
  };

  const handleTransportRecord = async () => {
    if (!armedChannel) return;

    if (countInRemaining != null) {
      clearCountInTimers();
      return;
    }

    if (isRecording) {
      clearPunchOutTimer();
      if (mediaRecorderRef?.current?.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      stopMetronomeClock();
      setIsRecording(false);
      return;
    }

    try {
      if (typeof MediaRecorder === 'undefined') {
        setRecordingError('This browser does not support audio recording. Use Chrome, Edge, or Safari.');
        return;
      }
      await initializeAudioContext();
      if (!mediaStreamRef.current) return;
      const punchRange = isPunchInEnabled ? getPunchRange() : { start: currentTime, end: null };
      if (countInEnabled) {
        setRecordingError(null);
        runCountIn(() => startRecordingCapture(punchRange));
      } else {
        await startRecordingCapture(punchRange);
      }
    } catch (error) {
      console.error('Recording prepare failed:', error);
      setRecordingError('Recording could not start. Check microphone permission and input device.');
    }
  };

  const handleTransportRewind = () => {
    seekTimeline(Math.max(0, currentTime - 5), { restartPlayback: true });
  };

  const handleTransportFastForward = () => {
    seekTimeline(Math.min(getProjectDuration(), currentTime + 5), { restartPlayback: true });
  };

  const handleBeatImport = (info) => {
    setBeatInfo(info);
    // Immediately sync beat key notes to the autotune DSP if a worklet node exists
    if (pitchProcessorRef?.current && info?.keyInfo?.notes) {
      updateAutotuneParams(pitchProcessorRef?.current, {
        beatKeyNotes: info?.keyInfo?.notes,
      });
    }
  };

  const handleAudioReady = () => {
    // Beat audio is ready
  };

  const handleBeatTimeUpdate = (time) => {
    setCurrentTime(time);
  };

  const handleMonitoringToggle = async () => {
    const next = !monitoringEnabled;
    setMonitoringEnabled(next);
    if (!mediaStreamRef.current && next) {
      await initializeAudioContext();
      if (monitoringGainRef.current) {
        monitoringGainRef.current.gain.value = 1.0;
      }
      return;
    }
    if (monitoringGainRef.current) {
      monitoringGainRef.current.gain.value = next ? 1.0 : 0.0;
    }
  };

  const editWindowSections = [
    { id: 'verse1', label: 'Verse 1', channels: verse1Channels },
    { id: 'verse2', label: 'Verse 2', channels: verse2Channels },
    { id: 'verse3', label: 'Verse 3', channels: verse3Channels },
    { id: 'hook', label: 'Hook', channels: hookChannels },
  ];

  const handleArm = (sectionId, channelId) => {
    setArmedChannel((current) =>
      current?.sectionId === sectionId && current?.channelId === channelId
        ? null
        : { sectionId, channelId }
    );
  };

  const handleMuteToggle = (sectionId, channelId) => {
    pushUndoSnapshot();
    const updateChannels = (channels) =>
      channels?.map(ch => ch?.id === channelId ? { ...ch, muted: !ch?.muted } : ch);
    
    if (sectionId === 'verse1') setVerse1Channels(updateChannels);
    else if (sectionId === 'verse2') setVerse2Channels(updateChannels);
    else if (sectionId === 'verse3') setVerse3Channels(updateChannels);
    else if (sectionId === 'hook') setHookChannels(updateChannels);
  };

  const handleSoloToggle = (sectionId, channelId) => {
    pushUndoSnapshot();
    const updateChannels = (channels) =>
      channels?.map(ch => ch?.id === channelId ? { ...ch, soloed: !ch?.soloed } : ch);
    
    if (sectionId === 'verse1') setVerse1Channels(updateChannels);
    else if (sectionId === 'verse2') setVerse2Channels(updateChannels);
    else if (sectionId === 'verse3') setVerse3Channels(updateChannels);
    else if (sectionId === 'hook') setHookChannels(updateChannels);
  };

  const handleAudition = (sectionId, channelId) => {
    setAuditionChannels(prev => ({
      ...prev,
      [`${sectionId}-${channelId}`]: !prev?.[`${sectionId}-${channelId}`]
    }));
  };

  const handlePlayback = async (sectionId, channelId) => {
    const channelKey = `${sectionId}-${channelId}`;
    const existingPlayer = audioPlayersRef.current?.[channelKey];
    if (existingPlayer) {
      stopTrackPlayback(channelKey);
      return;
    }

    const channel = getSectionChannel(sectionId, channelId);
    if (!channel?.filePath) return;

    try {
      const context = audioContextRef.current || new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = context;
      if (context.state === 'suspended') await context.resume();

      let buffer = audioBuffersRef.current?.[`${channelKey}:${channel.selectedTakeId}`] || audioBuffersRef.current?.[channelKey];
      if (!buffer) {
        const response = await fetch(channel.filePath);
        if (!response.ok) throw new Error('Recorded take could not be loaded.');
        buffer = await context.decodeAudioData(await response.arrayBuffer());
        audioBuffersRef.current[channelKey] = buffer;
        if (channel.selectedTakeId) audioBuffersRef.current[`${channelKey}:${channel.selectedTakeId}`] = buffer;
      }

      const clipStart = Math.max(0, Number(channel?.clipStart) || 0);
      const clipEnd = Math.max(clipStart, Number(channel?.clipEnd ?? channel?.duration) || channel?.duration || clipStart);
      const bufferDuration = Number(buffer.duration) || 0;
      const boundedOffset = Math.max(0, Math.min(clipStart, bufferDuration));
      const boundedDuration = Math.min(Math.max(0.05, clipEnd - clipStart), bufferDuration - boundedOffset);
      if (boundedOffset >= bufferDuration || boundedDuration <= 0.01) return;

      const source = context.createBufferSource();
      const gain = context.createGain();
      const panner = context.createStereoPanner ? context.createStereoPanner() : null;
      const volume = channel?.muted
        ? 0
        : Math.max(0, Math.min(1, (channel?.volume ?? 1) * (channelFaderVolumesRef.current?.[sectionId] ?? 1)));
      const pan = Math.max(-1, Math.min(1, Number(channel?.pan) || 0));

      source.buffer = buffer;
      gain.gain.value = volume;
      if (panner) {
        panner.pan.value = pan;
        source.connect(gain);
        gain.connect(panner);
        panner.connect(context.destination);
      } else {
        source.connect(gain);
        gain.connect(context.destination);
      }

      source.onended = () => stopTrackPlayback(channelKey);
      audioPlayersRef.current[channelKey] = {
        source,
        gain,
        panner,
        stop: () => {
          try {
            source.onended = null;
            source.stop();
          } catch (error) {
            // Source may already be stopped by the audio clock.
          }
          try {
            source.disconnect();
            gain.disconnect();
            panner?.disconnect();
          } catch (error) {
            // Nodes can already be disconnected during cleanup.
          }
        },
      };
      updateSectionChannel(sectionId, channelId, (current) => ({
        ...current,
        isPlaying: true,
      }));

      source.start(0, boundedOffset, boundedDuration);
      audioPlayerTimeoutsRef.current[channelKey] = setTimeout(() => {
        stopTrackPlayback(channelKey);
      }, boundedDuration * 1000);
    } catch (error) {
      console.error('Track playback failed:', error);
      stopTrackPlayback(channelKey);
      setRecordingError('Playback could not start. Try clicking the track play button again.');
    }
  };

  const handleDelete = (sectionId, channelId) => {
    const channelKey = `${sectionId}-${channelId}`;
    const channel = getSectionChannel(sectionId, channelId);
    stopTrackPlayback(channelKey);
    if (channel?.filePath?.startsWith('blob:')) {
      URL.revokeObjectURL(channel.filePath);
    }

    const updateChannels = (channels) =>
      channels?.map(ch => ch?.id === channelId ? {
        ...ch,
        recorded: false,
        isPlaying: false,
        filePath: null,
        duration: null,
        clipStart: null,
        clipEnd: null,
        clipOffset: null,
        selectedTakeId: null,
      } : ch);
    
    if (sectionId === 'verse1') setVerse1Channels(updateChannels);
    else if (sectionId === 'verse2') setVerse2Channels(updateChannels);
    else if (sectionId === 'verse3') setVerse3Channels(updateChannels);
    else if (sectionId === 'hook') setHookChannels(updateChannels);

    setRecordedWaveforms((current) => {
      const next = { ...current };
      delete next[channelKey];
      return next;
    });
    setTakes((current) => {
      const next = { ...current };
      next?.[channelKey]?.forEach((take) => {
        if (take?.blobUrl?.startsWith('blob:')) URL.revokeObjectURL(take.blobUrl);
        delete audioBuffersRef.current[`${channelKey}:${take?.id}`];
      });
      delete audioBuffersRef.current[channelKey];
      delete next[channelKey];
      return next;
    });
  };

  const handlePanChange = (sectionId, channelId, pan) => {
    const updateChannels = (channels) =>
      channels?.map(ch => ch?.id === channelId ? { ...ch, pan } : ch);
    
    if (sectionId === 'verse1') setVerse1Channels(updateChannels);
    else if (sectionId === 'verse2') setVerse2Channels(updateChannels);
    else if (sectionId === 'verse3') setVerse3Channels(updateChannels);
    else if (sectionId === 'hook') setHookChannels(updateChannels);

    Object.entries(audioPlayersRef.current || {}).forEach(([playerKey, player]) => {
      if (playerKey !== `${sectionId}-${channelId}` && !playerKey?.startsWith(`${sectionId}-${channelId}::`)) return;
      if (player?.panner) {
        player.panner.pan.value = Math.max(-1, Math.min(1, Number(pan) || 0));
      }
    });
  };

  const handleTrackVolumeChange = (sectionId, channelId, volume) => {
    const updateChannels = (channels) =>
      channels?.map(ch => ch?.id === channelId ? { ...ch, volume } : ch);
    
    if (sectionId === 'verse1') setVerse1Channels(updateChannels);
    else if (sectionId === 'verse2') setVerse2Channels(updateChannels);
    else if (sectionId === 'verse3') setVerse3Channels(updateChannels);
    else if (sectionId === 'hook') setHookChannels(updateChannels);

    Object.entries(audioPlayersRef.current || {}).forEach(([playerKey, player]) => {
      if (playerKey !== `${sectionId}-${channelId}` && !playerKey?.startsWith(`${sectionId}-${channelId}::`)) return;
      if (player?.gain) {
        player.gain.gain.value = Math.max(0, Math.min(1, (Number(volume) || 0) * (channelFaderVolumesRef.current?.[sectionId] ?? 1)));
      }
    });
  };

  const handleSelectTake = (channelKey, takeId) => {
    const { sectionId, channelId } = parseChannelKey(channelKey);
    const selectedTake = takes?.[channelKey]?.find((take) => take?.id === takeId);
    pushUndoSnapshot();

    setTakes(prev => ({
      ...prev,
      [channelKey]: prev?.[channelKey]?.map((take) => {
        const isSelected = take?.id === takeId;
        return {
          ...take,
          isSelected,
          timelineActive: isSelected ? true : take?.timelineActive,
        };
      }) || [],
    }));

    if (!sectionId || !channelId || !selectedTake) return;
    stopTrackPlayback(channelKey);
    const selectedBuffer = audioBuffersRef.current?.[`${channelKey}:${takeId}`];
    if (selectedBuffer) audioBuffersRef.current[channelKey] = selectedBuffer;
    setRecordedWaveforms((current) => ({
      ...current,
      [channelKey]: selectedTake?.waveform || [],
    }));
    updateSectionChannel(sectionId, channelId, (channel) => ({
      ...channel,
      recorded: Boolean(selectedTake?.blobUrl || selectedTake?.remoteUrl || selectedTake?.remotePath),
      isPlaying: false,
      duration: selectedTake?.duration ?? null,
      filePath: selectedTake?.blobUrl || resolveAssetUrl(selectedTake?.remoteUrl || selectedTake?.remotePath) || null,
      selectedTakeId: selectedTake?.id ?? null,
      clipStart: selectedTake?.clipStart ?? 0,
      clipEnd: selectedTake?.clipEnd ?? selectedTake?.duration ?? null,
      clipOffset: selectedTake?.clipOffset ?? 0,
    }));
  };

  const isBlobUrlUsedByOtherTake = (blobUrl, takeIdToIgnore = null) => {
    if (!blobUrl?.startsWith?.('blob:')) return false;
    return Object.values(takes || {}).some((channelTakes) =>
      channelTakes?.some((take) => take?.blobUrl === blobUrl && take?.id !== takeIdToIgnore)
    );
  };

  const handleDeleteTake = (channelKey, takeId) => {
    const { sectionId, channelId } = parseChannelKey(channelKey);
    const currentTakes = takes?.[channelKey] || [];
    const takeToDelete = currentTakes.find((take) => take?.id === takeId);
    if (!takeToDelete) return;
    pushUndoSnapshot();
    stopTrackPlayback(channelKey);
    if (takeToDelete?.blobUrl?.startsWith('blob:') && !isBlobUrlUsedByOtherTake(takeToDelete.blobUrl, takeId)) {
      URL.revokeObjectURL(takeToDelete.blobUrl);
    }
    delete audioBuffersRef.current[`${channelKey}:${takeId}`];

    const remaining = currentTakes.filter((take) => take?.id !== takeId);
    const nextSelected = remaining.find((take) => take?.isSelected) || remaining[remaining.length - 1] || null;
    const normalized = remaining.map((take) => ({
      ...take,
      isSelected: nextSelected ? take?.id === nextSelected?.id : false,
    }));

    if (sectionId && channelId) {
      setRecordedWaveforms((current) => {
        const next = { ...current };
        if (nextSelected?.waveform) next[channelKey] = nextSelected.waveform;
        else delete next[channelKey];
        return next;
      });
      updateSectionChannel(sectionId, channelId, (channel) => ({
        ...channel,
        recorded: Boolean(nextSelected?.blobUrl || nextSelected?.remoteUrl || nextSelected?.remotePath),
        isPlaying: false,
        duration: nextSelected?.duration ?? null,
        filePath: nextSelected?.blobUrl || resolveAssetUrl(nextSelected?.remoteUrl || nextSelected?.remotePath) || null,
        selectedTakeId: nextSelected?.id ?? null,
        clipStart: nextSelected?.clipStart ?? 0,
        clipEnd: nextSelected?.clipEnd ?? nextSelected?.duration ?? null,
        clipOffset: nextSelected?.clipOffset ?? 0,
      }));
      if (nextSelected?.id && audioBuffersRef.current?.[`${channelKey}:${nextSelected.id}`]) {
        audioBuffersRef.current[channelKey] = audioBuffersRef.current[`${channelKey}:${nextSelected.id}`];
      } else {
        delete audioBuffersRef.current[channelKey];
      }
    }

    setTakes(prev => {
      const next = { ...prev };
      if (normalized.length) next[channelKey] = normalized;
      else delete next[channelKey];
      return next;
    });
  };

  const handleDeleteSelectedClip = () => {
    const target = findEditableTakeAtPlayhead();
    if (!target?.channelKey || !target?.take) {
      setRecordingError('Select a recorded take before deleting.');
      return;
    }
    handleDeleteTake(target.channelKey, target.take.id);
    setRecordingError(null);
  };

  const handleRenameTake = (channelKey, takeId, newName) => {
    const trimmedName = newName?.trim();
    if (!trimmedName) return;
    pushUndoSnapshot();
    setTakes(prev => ({
      ...prev,
      [channelKey]: prev?.[channelKey]?.map((take) =>
        take?.id === takeId ? { ...take, name: trimmedName } : take
      )
    }));
  };

  const handleToggleTakeTimelineActive = (channelKey, takeId) => {
    const { sectionId, channelId } = parseChannelKey(channelKey);
    const currentTake = takes?.[channelKey]?.find((take) => take?.id === takeId);
    if (!currentTake) return;

    const nextActive = !currentTake?.timelineActive;
    pushUndoSnapshot();
    stopTrackPlayback(channelKey);

    setTakes((prev) => ({
      ...prev,
      [channelKey]: (prev?.[channelKey] || []).map((take) => ({
        ...take,
        isSelected: nextActive ? take?.id === takeId : take?.isSelected,
        timelineActive: take?.id === takeId ? nextActive : take?.timelineActive,
      })),
    }));

    if (nextActive && sectionId && channelId) {
      const selectedBuffer = audioBuffersRef.current?.[`${channelKey}:${takeId}`];
      if (selectedBuffer) audioBuffersRef.current[channelKey] = selectedBuffer;
      setRecordedWaveforms((current) => ({
        ...current,
        [channelKey]: currentTake?.waveform || [],
      }));
      updateSectionChannel(sectionId, channelId, (channel) => ({
        ...channel,
        recorded: true,
        isPlaying: false,
        duration: currentTake?.duration ?? channel?.duration ?? null,
        filePath: currentTake?.blobUrl || resolveAssetUrl(currentTake?.remoteUrl || currentTake?.remotePath) || channel?.filePath || null,
        selectedTakeId: takeId,
        clipStart: currentTake?.clipStart ?? 0,
        clipEnd: currentTake?.clipEnd ?? currentTake?.duration ?? null,
        clipOffset: currentTake?.clipOffset ?? 0,
      }));
    }
  };

  const handlePromoteTakeToComp = (channelKey, takeId) => {
    const { sectionId, channelId } = parseChannelKey(channelKey);
    const currentTake = takes?.[channelKey]?.find((take) => take?.id === takeId);
    if (!currentTake) return;

    pushUndoSnapshot();
    stopTrackPlayback(channelKey);

    setTakes((prev) => ({
      ...prev,
      [channelKey]: (prev?.[channelKey] || []).map((take) => ({
        ...take,
        isSelected: take?.id === takeId,
        timelineActive: take?.id === takeId,
      })),
    }));

    if (sectionId && channelId) {
      const selectedBuffer = audioBuffersRef.current?.[`${channelKey}:${takeId}`];
      if (selectedBuffer) audioBuffersRef.current[channelKey] = selectedBuffer;
      setRecordedWaveforms((current) => ({
        ...current,
        [channelKey]: currentTake?.waveform || [],
      }));
      updateSectionChannel(sectionId, channelId, (channel) => ({
        ...channel,
        recorded: Boolean(currentTake?.blobUrl || currentTake?.remoteUrl || currentTake?.remotePath || currentTake?.filePath),
        isPlaying: false,
        duration: currentTake?.duration ?? channel?.duration ?? null,
        filePath: currentTake?.blobUrl || resolveAssetUrl(currentTake?.remoteUrl || currentTake?.remotePath) || currentTake?.filePath || channel?.filePath || null,
        selectedTakeId: takeId,
        clipStart: currentTake?.clipStart ?? 0,
        clipEnd: currentTake?.clipEnd ?? currentTake?.duration ?? null,
        clipOffset: currentTake?.clipOffset ?? 0,
      }));
    }

    setSaveNotice({ type: 'success', message: 'Take promoted as the active comp.' });
  };

  const handleDuplicateTake = (channelKey, takeId) => {
    const { sectionId, channelId } = parseChannelKey(channelKey);
    const currentTakes = takes?.[channelKey] || [];
    const sourceTake = currentTakes.find((take) => take?.id === takeId);
    if (!sourceTake) return;

    pushUndoSnapshot();
    stopTrackPlayback(channelKey);
    const duplicateId = makeTakeId();
    const duplicateTake = {
      ...cloneHistoryValue(sourceTake),
      id: duplicateId,
      name: `${sourceTake?.name || 'Take'} Copy`,
      createdAt: new Date().toISOString(),
      backendFileId: null,
      isSelected: true,
      timelineActive: true,
      clipOffset: Math.max(0, Number(sourceTake?.clipOffset || 0) + 0.25),
    };

    const sourceBuffer = audioBuffersRef.current?.[`${channelKey}:${takeId}`];
    if (sourceBuffer) {
      audioBuffersRef.current[`${channelKey}:${duplicateId}`] = sourceBuffer;
      audioBuffersRef.current[channelKey] = sourceBuffer;
    }

    setTakes(prev => ({
      ...prev,
      [channelKey]: [
        ...(prev?.[channelKey] || []).map((take) => ({ ...take, isSelected: false })),
        duplicateTake,
      ],
    }));

    setRecordedWaveforms((current) => ({
      ...current,
      [channelKey]: duplicateTake?.waveform || [],
    }));

    if (sectionId && channelId) {
      updateSectionChannel(sectionId, channelId, (channel) => ({
        ...channel,
        recorded: Boolean(duplicateTake?.blobUrl || duplicateTake?.remoteUrl || duplicateTake?.remotePath),
        isPlaying: false,
        duration: duplicateTake?.duration ?? channel?.duration ?? null,
        filePath: duplicateTake?.blobUrl || resolveAssetUrl(duplicateTake?.remoteUrl || duplicateTake?.remotePath) || channel?.filePath || null,
        selectedTakeId: duplicateId,
        clipStart: duplicateTake?.clipStart ?? 0,
        clipEnd: duplicateTake?.clipEnd ?? duplicateTake?.duration ?? null,
        clipOffset: duplicateTake?.clipOffset ?? 0,
      }));
    }
  };

  const findEditableTakeAtPlayhead = () => {
    const candidates = Object.entries(takes || {}).flatMap(([channelKey, channelTakes]) =>
      (channelTakes || [])
        .filter((take) => take?.isSelected)
        .map((take) => ({ channelKey, take, range: getTakeClipRange(take) }))
    );

    if (!candidates.length) return null;
    const armedKey = armedChannel ? `${armedChannel.sectionId}-${armedChannel.channelId}` : null;
    const prioritized = armedKey
      ? [
          ...candidates.filter((candidate) => candidate.channelKey === armedKey),
          ...candidates.filter((candidate) => candidate.channelKey !== armedKey),
        ]
      : candidates;

    return prioritized.find((candidate) =>
      currentTime > candidate.range.timelineStart + 0.05 &&
      currentTime < candidate.range.timelineEnd - 0.05
    ) || prioritized[0];
  };

  const handleSplitTakeAtPlayhead = (channelKeyOverride = null, takeIdOverride = null) => {
    const explicitTake = channelKeyOverride && takeIdOverride
      ? {
          channelKey: channelKeyOverride,
          take: takes?.[channelKeyOverride]?.find((take) => take?.id === takeIdOverride),
        }
      : null;
    const target = explicitTake?.take ? explicitTake : findEditableTakeAtPlayhead();
    if (!target?.channelKey || !target?.take) {
      setRecordingError('Select a recorded take before splitting.');
      return;
    }

    const { channelKey, take: sourceTake } = target;
    const { sectionId, channelId } = parseChannelKey(channelKey);
    const range = getTakeClipRange(sourceTake);
    const splitTimelineTime = Math.max(range.timelineStart, Math.min(currentTime, range.timelineEnd));
    const splitSourceTime = range.clipStart + (splitTimelineTime - range.timelineStart);

    if (
      splitTimelineTime <= range.timelineStart + 0.05 ||
      splitTimelineTime >= range.timelineEnd - 0.05 ||
      splitSourceTime <= range.clipStart + 0.05 ||
      splitSourceTime >= range.clipEnd - 0.05
    ) {
      setRecordingError('Move the playhead inside the selected clip before splitting.');
      return;
    }

    pushUndoSnapshot();
    stopTrackPlayback(channelKey);

    const splitId = makeTakeId();
    const rightTake = {
      ...cloneHistoryValue(sourceTake),
      id: splitId,
      name: `${sourceTake?.name || 'Take'} Split`,
      createdAt: new Date().toISOString(),
      isSelected: true,
      timelineActive: true,
      clipStart: splitSourceTime,
      clipEnd: range.clipEnd,
      clipOffset: splitTimelineTime,
    };
    const leftTake = {
      ...sourceTake,
      isSelected: false,
      timelineActive: true,
      clipStart: range.clipStart,
      clipEnd: splitSourceTime,
      clipOffset: range.clipOffset,
    };

    const sourceBuffer = audioBuffersRef.current?.[`${channelKey}:${sourceTake.id}`] || audioBuffersRef.current?.[channelKey];
    if (sourceBuffer) {
      audioBuffersRef.current[`${channelKey}:${sourceTake.id}`] = sourceBuffer;
      audioBuffersRef.current[`${channelKey}:${splitId}`] = sourceBuffer;
      audioBuffersRef.current[channelKey] = sourceBuffer;
    }

    setTakes((prev) => ({
      ...prev,
      [channelKey]: [
        ...(prev?.[channelKey] || []).map((take) =>
          take?.id === sourceTake.id ? leftTake : { ...take, isSelected: false }
        ),
        rightTake,
      ],
    }));

    setRecordedWaveforms((current) => ({
      ...current,
      [channelKey]: rightTake?.waveform || sourceTake?.waveform || [],
    }));

    if (sectionId && channelId) {
      updateSectionChannel(sectionId, channelId, (channel) => ({
        ...channel,
        recorded: Boolean(rightTake?.blobUrl || rightTake?.remoteUrl || rightTake?.remotePath),
        isPlaying: false,
        duration: rightTake?.duration ?? channel?.duration ?? null,
        filePath: rightTake?.blobUrl || resolveAssetUrl(rightTake?.remoteUrl || rightTake?.remotePath) || channel?.filePath || null,
        selectedTakeId: splitId,
        clipStart: rightTake.clipStart,
        clipEnd: rightTake.clipEnd,
        clipOffset: rightTake.clipOffset,
      }));
    }
    setRecordingError(null);
  };

  const getNudgeAmountSeconds = () => {
    if (!snapEnabled) return 0.1;
    const bpmValue = Number(beatInfo?.bpm) || 120;
    return Math.max(0.01, (60 / bpmValue) * (Number(gridSize) || 1));
  };

  const handleNudgeSelectedClip = (direction) => {
    const target = findEditableTakeAtPlayhead();
    if (!target?.channelKey || !target?.take) {
      setRecordingError('Select a recorded take before nudging.');
      return;
    }

    const amount = getNudgeAmountSeconds();
    const currentOffset = Math.max(0, Number(target.take?.clipOffset ?? 0) || 0);
    const nextOffset = Math.max(0, currentOffset + (direction < 0 ? -amount : amount));
    pushUndoSnapshot();
    stopTrackPlayback(target.channelKey);
    handleUpdateTakeClip(target.channelKey, target.take.id, { clipOffset: nextOffset });
    seekTimeline(nextOffset, { restartPlayback: false });
    setRecordingError(null);
  };

  const handleResetTakeClip = (channelKey, takeId) => {
    const currentTake = takes?.[channelKey]?.find((take) => take?.id === takeId);
    if (!currentTake) return;
    pushUndoSnapshot();
    handleUpdateTakeClip(channelKey, takeId, {
      clipStart: 0,
      clipEnd: currentTake?.duration ?? 0,
      clipOffset: 0,
    });
  };

  const handleUpdateTakeClip = (channelKey, takeId, clipUpdates) => {
    const { sectionId, channelId } = parseChannelKey(channelKey);
    const currentTake = takes?.[channelKey]?.find((take) => take?.id === takeId);
    if (!currentTake) return;

    const nextClip = normalizeClipSettings({
      clipStart: currentTake?.clipStart ?? 0,
      clipEnd: currentTake?.clipEnd ?? currentTake?.duration,
      clipOffset: currentTake?.clipOffset ?? 0,
      ...clipUpdates,
    }, currentTake?.duration);

    setTakes(prev => ({
      ...prev,
      [channelKey]: prev?.[channelKey]?.map((take) =>
        take?.id === takeId ? { ...take, ...nextClip } : take
      )
    }));

    if (currentTake?.isSelected && sectionId && channelId) {
      stopTrackPlayback(channelKey);
      updateSectionChannel(sectionId, channelId, (channel) => ({
        ...channel,
        ...nextClip,
      }));
    }
  };

  const handleUpdateChannelClip = (sectionId, channelId, clipUpdates, takeIdOverride = null) => {
    const channelKey = `${sectionId}-${channelId}`;
    const channel = getSectionChannel(sectionId, channelId);
    const selectedTakeId = takeIdOverride
      || channel?.selectedTakeId
      || takes?.[channelKey]?.find((take) => take?.isSelected)?.id
      || takes?.[channelKey]?.[0]?.id;
    if (!selectedTakeId) return;
    handleUpdateTakeClip(channelKey, selectedTakeId, clipUpdates);
  };

  const handleBeginEditHistory = () => {
    pushUndoSnapshot();
  };

  const handleFaderVolumeChange = (channel, volume) => {
    setChannelFaderVolumes(prev => ({ ...prev, [channel]: volume }));
    channelFaderVolumesRef.current = { ...channelFaderVolumesRef?.current, [channel]: volume };
    Object.entries(audioPlayersRef.current || {}).forEach(([playerKey, player]) => {
      const { sectionId, channelId } = parseChannelKey(String(playerKey || '').split('::')[0]);
      if (sectionId !== channel || !player?.gain) return;
      const sourceChannel = getSectionChannel(sectionId, channelId);
      player.gain.gain.value = Math.max(0, Math.min(1, (sourceChannel?.volume ?? 1) * volume));
    });
  };

  const handleApplyTemplate = (template) => {
    // Apply project template - only sets genre/autotune context, NOT vocal preset
    // Vocal preset is independently controlled by the user via PresetSelector
    setSelectedTemplateId(template?.id);
    // Apply autotune setting from template
    if (template?.autotune !== undefined) setAutotuneEnabled(template?.autotune);
    if (template?.retuneSpeed !== undefined) setRetuneSpeed(template?.retuneSpeed);
  };

  const handleVocalFxChange = (settings) => {
    setVocalFxSettings({ ...DEFAULT_VOCAL_FX_SETTINGS, ...(settings || {}) });
  };

  const handleDeviceChange = async (deviceId) => {
    setSelectedDevice(deviceId);
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks()?.forEach((track) => track.stop());
      mediaStreamRef.current = null;
      micSourceRef.current = null;
    }
    if (armedChannel || monitoringEnabled || isRecording) {
      await initializeAudioContext(false, deviceId);
    }
  };

  const handleMixerChannelUpdate = (sectionTitle) => (channelId, updates) => {
    const updateChannels = (channels) =>
      channels?.map(ch => ch?.id === channelId ? { ...ch, ...updates } : ch);
    
    if (sectionTitle === 'Verse 1') setVerse1Channels(updateChannels);
    else if (sectionTitle === 'Verse 2') setVerse2Channels(updateChannels);
    else if (sectionTitle === 'Verse 3') setVerse3Channels(updateChannels);
    else if (sectionTitle === 'Hook') setHookChannels(updateChannels);
  };

  const exportSections = [
    { id: 'verse1', label: 'Verse 1', channels: verse1Channels },
    { id: 'verse2', label: 'Verse 2', channels: verse2Channels },
    { id: 'verse3', label: 'Verse 3', channels: verse3Channels },
    { id: 'hook', label: 'Hook', channels: hookChannels },
  ];

  const exportChannels = exportSections.flatMap((section) =>
    section.channels.map((channel) => ({
      ...channel,
      sectionId: section.id,
      sectionLabel: section.label,
      channelKey: `${section.id}-${channel.id}`,
    }))
  );

  const allChannelsFlat = exportChannels;
  const mixPlaybackSignature = useMemo(() => (
    exportChannels.map((channel) => [
      channel.channelKey,
      channel.recorded ? 1 : 0,
      channel.muted ? 1 : 0,
      channel.soloed ? 1 : 0,
      Number(channel.volume ?? 1).toFixed(3),
      Number(channel.pan ?? 0).toFixed(3),
      Number(channelFaderVolumes?.[channel.sectionId] ?? 1).toFixed(3),
    ].join(':')).join('|')
  ), [verse1Channels, verse2Channels, verse3Channels, hookChannels, channelFaderVolumes]);
  const snapSeconds = useMemo(() => {
    const bpmValue = Number(beatInfo?.bpm) || 120;
    return Math.max(0.01, (60 / bpmValue) * (Number(gridSize) || 1));
  }, [beatInfo?.bpm, gridSize]);

  const connectOfflineVocalFx = (offlineContext, source, destination) => {
    const settings = { ...DEFAULT_VOCAL_FX_SETTINGS, ...(vocalFxSettings || {}) };
    if (!settings.enabled) {
      source.connect(destination);
      return;
    }

    const highpass = offlineContext.createBiquadFilter();
    highpass.type = 'highpass';
    highpass.frequency.value = Math.max(40, Math.min(220, Number(settings.highpassHz) || 80));
    highpass.Q.value = 0.7;

    const compressor = offlineContext.createDynamicsCompressor();
    const compression = Math.max(0, Math.min(100, Number(settings.compression) || 0));
    compressor.threshold.value = -14 - (compression * 0.24);
    compressor.knee.value = 18;
    compressor.ratio.value = 1.5 + (compression / 100) * 5.5;
    compressor.attack.value = 0.004;
    compressor.release.value = 0.18;

    const presence = offlineContext.createBiquadFilter();
    presence.type = 'peaking';
    presence.frequency.value = 4200;
    presence.Q.value = 0.9;
    presence.gain.value = ((Math.max(0, Math.min(100, Number(settings.presence) || 0)) - 50) / 50) * 5;

    const dryGain = offlineContext.createGain();
    dryGain.gain.value = 1;

    const wetSum = offlineContext.createGain();
    wetSum.gain.value = 1;

    source.connect(highpass);
    highpass.connect(compressor);
    compressor.connect(presence);
    presence.connect(dryGain);
    dryGain.connect(destination);

    const reverbAmount = Math.max(0, Math.min(100, Number(settings.reverb) || 0)) / 100;
    if (reverbAmount > 0.01) {
      const delay = offlineContext.createDelay(1);
      const gain = offlineContext.createGain();
      delay.delayTime.value = 0.075;
      gain.gain.value = reverbAmount * 0.22;
      presence.connect(delay);
      delay.connect(gain);
      gain.connect(wetSum);
    }

    const delayAmount = Math.max(0, Math.min(100, Number(settings.delay) || 0)) / 100;
    if (delayAmount > 0.01) {
      const slap = offlineContext.createDelay(1);
      const feedback = offlineContext.createGain();
      const gain = offlineContext.createGain();
      slap.delayTime.value = 0.18;
      feedback.gain.value = 0.18;
      gain.gain.value = delayAmount * 0.28;
      presence.connect(slap);
      slap.connect(feedback);
      feedback.connect(slap);
      slap.connect(gain);
      gain.connect(wetSum);
    }

    wetSum.connect(destination);
  };

  const handleBounce = async ({ format, sampleRate }) => {
    const targetSampleRate = Number(sampleRate) || 44100;
    const recordedChannels = exportChannels.filter(isChannelAudibleInMix);
    const beatBuffer = beatImporterRef?.current?.getAudioBuffer?.() || beatInfo?.audioBuffer || null;
    const beatVolume = beatImporterRef?.current?.getVolume?.() ?? (beatVolumeState / 100);

    if (!recordedChannels.length && !beatBuffer) {
      throw new Error('Nothing to export yet. Record a take or import a beat first.');
    }

    const sourceItems = [];
    if (beatBuffer) {
      sourceItems.push({
        buffer: beatBuffer,
        volume: Math.max(0, Math.min(1, beatVolume ?? 0.8)),
        pan: 0,
        startAt: 0,
        clipStart: 0,
        clipDuration: beatBuffer?.duration || 0,
      });
    }

    recordedChannels.forEach((channel) => {
      getTimelineTakesForChannel(channel).forEach((take) => {
        const buffer = audioBuffersRef.current?.[`${channel.channelKey}:${take.id}`] || audioBuffersRef.current?.[channel.channelKey];
        if (!buffer) return;
        const { clipStart, clipEnd, clipOffset } = getTakeClipRange({ ...take, duration: take?.duration ?? buffer.duration });
        sourceItems.push({
          buffer,
          volume: Math.max(0, Math.min(1, (channel?.volume ?? 1) * (channelFaderVolumesRef.current?.[channel.sectionId] ?? 1))),
          pan: Math.max(-1, Math.min(1, channel?.pan ?? 0)),
          startAt: clipOffset,
          clipStart,
          clipDuration: Math.max(0.01, clipEnd - clipStart),
          isVocal: true,
        });
      });
    });

    if (!sourceItems.length) {
      throw new Error('Recorded audio is not loaded in this browser session. Re-record or reload the project assets.');
    }

    const renderDuration = Math.max(...sourceItems.map((item) => (item.startAt || 0) + (item.clipDuration || item.buffer?.duration || 0)), 1);
    const frameCount = Math.ceil(renderDuration * targetSampleRate);
    const offlineContext = new OfflineAudioContext(2, frameCount, targetSampleRate);

    sourceItems.forEach((item) => {
      const source = offlineContext.createBufferSource();
      const gain = offlineContext.createGain();
      const panner = offlineContext.createStereoPanner();
      source.buffer = item.buffer;
      gain.gain.value = item.volume;
      panner.pan.value = item.pan;
      if (item.isVocal) {
        connectOfflineVocalFx(offlineContext, source, gain);
      } else {
        source.connect(gain);
      }
      gain.connect(panner);
      panner.connect(offlineContext.destination);
      source.start(item.startAt || 0, item.clipStart || 0, item.clipDuration || undefined);
    });

    const renderedBuffer = await offlineContext.startRendering();
    const blob = audioBufferToWav(renderedBuffer, getFormatBitDepth(format));
    return {
      blob,
      extension: 'wav',
      mimeType: 'audio/wav',
      fileName: `${sessionTitle || 'recording-studio'}_${Date.now()}.wav`,
    };
  };

  const stopInputMeter = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  };

  const startInputMeter = () => {
    stopInputMeter();
    const analyser = analyserRef.current;
    if (!analyser) return;

    if (!meterDataRef.current || meterDataRef.current.length !== analyser.fftSize) {
      meterDataRef.current = new Uint8Array(analyser.fftSize);
    }

    const updateMeter = () => {
      if (!analyserRef.current || !meterDataRef.current) return;
      analyserRef.current.getByteTimeDomainData(meterDataRef.current);

      let sumSquares = 0;
      for (let i = 0; i < meterDataRef.current.length; i++) {
        const centered = (meterDataRef.current[i] - 128) / 128;
        sumSquares += centered * centered;
      }

      const rms = Math.sqrt(sumSquares / meterDataRef.current.length);
      const nextLevel = Math.min(100, Math.max(0, rms * 180));
      const now = performance.now();
      if (
        now - meterLastUiUpdateRef.current > 100 ||
        Math.abs(nextLevel - inputLevelRef.current) > 5
      ) {
        inputLevelRef.current = nextLevel;
        meterLastUiUpdateRef.current = now;
        setInputLevel(nextLevel);
      }
      animationFrameRef.current = requestAnimationFrame(updateMeter);
    };

    updateMeter();
  };

  const initializeAudioContext = async (retry = false, deviceIdOverride = null) => {
    try {
      if (!audioContextRef?.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      const deviceId = deviceIdOverride || selectedDevice;
      const constraints = {
        audio: deviceId && deviceId !== 'default'
          ? {
              deviceId: { exact: deviceId },
              echoCancellation: false,
              noiseSuppression: false,
              autoGainControl: false,
            }
          : {
              echoCancellation: false,
              noiseSuppression: false,
              autoGainControl: false,
            },
      };

      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks()?.forEach((track) => track.stop());
      }

      const stream = await navigator.mediaDevices?.getUserMedia(constraints);
      mediaStreamRef.current = stream;

      // Tear down any previous autotune chain
      if (autotuneChainRef.current?.cleanup) {
        try { autotuneChainRef.current.cleanup(); } catch (e) {}
        autotuneChainRef.current = null;
      }

      // If autotune is enabled AND vocal preset is melodic, build the autotune chain.
      // This routes the raw mic through the worklet so monitoring + recording
      // both get the pitch-corrected signal.
      let activeStream = stream;
      if (autotuneEnabled && selectedPreset === "melodic") {
        try {
          const chain = await buildAutotuneChain(
            audioContextRef.current,
            stream,
            {
              enabled: true,
              wetMix: (autotuneWetMix ?? 80) / 100,
              dryMix: 1 - ((autotuneWetMix ?? 80) / 100),
              intensity: (autotuneIntensity ?? 100) / 100,
              retuneSpeed: (retuneSpeed ?? 50) / 100,
              beatKeyNotes: beatInfo?.keyScaleNotes || null,
            },
            null
          );
          if (chain?.processedStream) {
            autotuneChainRef.current = chain;
            activeStream = chain.processedStream;
            // MediaRecorder needs to record the processed stream, not the raw one
            mediaStreamRef.current = chain.processedStream;
            console.log("Autotune chain active — recording + monitoring will use processed stream");
          }
        } catch (e) {
          console.warn("Autotune chain build failed; using raw mic:", e?.message);
        }
      }

      if (micSourceRef.current) {
        try { micSourceRef.current.disconnect(); } catch (error) {}
      }
      if (monitoringGainRef.current) {
        try { monitoringGainRef.current.disconnect(); } catch (error) {}
      }

      const source = audioContextRef.current.createMediaStreamSource(activeStream);
      const analyser = audioContextRef.current.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.75;

      const monitorGain = audioContextRef.current.createGain();
      monitorGain.gain.value = monitoringEnabled ? 1 : 0;

      source.connect(analyser);
      source.connect(monitorGain);
      monitorGain.connect(audioContextRef.current.destination);

      micSourceRef.current = source;
      analyserRef.current = analyser;
      monitoringGainRef.current = monitorGain;
      startInputMeter();
      setMicPermissionError(null);
    } catch (error) {
      setMicPermissionError('Microphone access denied. Please allow microphone access to record.');
      console.error('Audio initialization error:', error);
    }
  };

  // Rebuild autotune chain when toggle/preset/key changes after mic is up
  useEffect(() => {
    if (!mediaStreamRef.current || !audioContextRef.current) return;
    // initializeAudioContext is idempotent in practice — call it to rebuild
    initializeAudioContext?.(selectedDevice).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autotuneEnabled, selectedPreset, beatInfo?.keyScaleNotes]);

  useEffect(() => {
    return () => {
      stopInputMeter();
      stopAllTrackPlayback();
      stopPlayheadClock();
      clearPunchOutTimer();
      clearCountInTimers();
      mediaStreamRef.current?.getTracks()?.forEach((track) => track.stop());
      try { micSourceRef.current?.disconnect(); } catch (error) {}
      try { monitoringGainRef.current?.disconnect(); } catch (error) {}
    };
  }, []);

  useEffect(() => {
    if (!user || authLoading) return;
    const checkSub = async () => {
      if (!REQUIRE_SUBSCRIPTION) {
        setHasSubscription(true);
        setSubscriptionChecked(true);
        return;
      }
      if (hasProAccess || isProUser) {
        setHasSubscription(true);
        setSubscriptionChecked(true);
        return;
      }
      if (authProvider === 'backend') {
        try {
          const token = localStorage.getItem('auth_token');
          const res = await fetch(`${BACKEND_API_URL}/stripe/status`, { headers: { Authorization: `Bearer ${token}` } });
          if (res.ok) {
            const json = await res.json();
            setHasSubscription(!!json?.data?.isActive);
          } else {
            setHasSubscription(false);
          }
        } catch (e) {
          setHasSubscription(false);
        } finally {
          setSubscriptionChecked(true);
        }
        return;
      }
      try {
        const { data } = await supabase?.functions?.invoke('verify-subscription', {
          body: { userId: user?.id },
        });
        if (data?.isActive) {
          setHasSubscription(true);
        } else {
          setHasSubscription(false);
        }
      } catch (err) {
        setHasSubscription(true);
      } finally {
        setSubscriptionChecked(true);
      }
    };
    checkSub();
  }, [user, authLoading, hasProAccess, isProUser, authProvider]);

  useEffect(() => {
    if (authLoading || !user || !subscriptionChecked || !hasSubscription) return;

    const params = new URLSearchParams(location.search || '');
    const sessionId =
      params.get('session') ||
      params.get('session_id') ||
      location?.state?.sessionId ||
      location?.state?.session?.id ||
      null;

    if (!sessionId || loadedSessionIdRef.current === sessionId) return;
    loadedSessionIdRef.current = sessionId;

    const loadSession = async () => {
      setLoadingSession(true);
      setRecordingError(null);
      try {
        const loadedFromBackend = await loadBackendSession(sessionId);
        if (loadedFromBackend) return;

        const { data, error } = await supabase
          ?.from('audio_sessions')
          ?.select('*')
          ?.eq('id', sessionId)
          ?.single();
        if (error) throw error;
        if (!data) throw new Error('Session not found');
        await hydrateSessionPayload({
          ...data,
          sessionData: data?.session_data,
          sessionTitle: data?.title,
        }, data?.title);
      } catch (error) {
        console.error('Cloud session load failed:', error);
        loadedSessionIdRef.current = null;
        setRecordingError(error?.message || 'Cloud session could not be loaded.');
      } finally {
        setLoadingSession(false);
      }
    };

    loadSession();
  }, [authLoading, user, subscriptionChecked, hasSubscription, location.search, location?.state]);

  useEffect(() => {
    let active = true;
    loadRecoverySnapshot()
      .then((snapshot) => {
        if (!active || !snapshot?.sessionData) return;
        setRecoverySnapshot(snapshot);
      })
      .catch((error) => console.error('Recovery check failed:', error));
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const hasRecoverableAudio =
      Boolean(beatInfo?.blobUrl || beatInfo?.remoteUrl || beatInfo?.publicUrl) ||
      Object.values(takes || {}).some((takesArr) => (takesArr || []).some((take) => take?.blobUrl || take?.filePath));
    const hasRecoverableSession =
      hasRecoverableAudio ||
      Object.values(takes || {}).some((takesArr) => (takesArr || []).length > 0) ||
      sessionTitle !== 'Untitled Session';

    if (!hasRecoverableSession) return undefined;

    const signature = JSON.stringify({
      sessionTitle,
      sessionId: currentSessionId,
      data: createSerializableSessionData(),
    });
    if (recoverySignatureRef.current === signature) return undefined;
    recoverySignatureRef.current = signature;

    if (recoveryTimerRef.current) window.clearTimeout(recoveryTimerRef.current);
    recoveryTimerRef.current = window.setTimeout(async () => {
      try {
        const snapshot = await buildRecoverySnapshot();
        await saveRecoverySnapshot(snapshot);
      } catch (error) {
        console.error('Recovery autosave failed:', error);
      }
    }, 4000);

    return () => {
      if (recoveryTimerRef.current) window.clearTimeout(recoveryTimerRef.current);
    };
  }, [
    sessionTitle,
    currentSessionId,
    beatInfo,
    takes,
    verse1Channels,
    verse2Channels,
    verse3Channels,
    hookChannels,
    selectedPreset,
    autotuneEnabled,
    retuneSpeed,
    autotuneIntensity,
    autotuneWetMix,
    vocalFxSettings,
    countInEnabled,
    countInBars,
    loopStartPoint,
    loopEndPoint,
    punchInPoint,
    punchOutPoint,
    isPunchInEnabled,
    isLoopEnabled,
    waveformZoom,
    snapEnabled,
    gridSize,
    recordedWaveforms,
    sessionNotes,
    sessionLyrics,
    timeMarkers,
  ]);

  useEffect(() => {
    if (!saveNotice) return undefined;
    const timer = window.setTimeout(() => setSaveNotice(null), 5000);
    return () => window.clearTimeout(timer);
  }, [saveNotice]);

  useEffect(() => {
    if (!currentSessionId || !getBackendAuthToken()) {
      setCloudExportJobs([]);
      return undefined;
    }

    let active = true;
    const refreshJobs = async (silent = true) => {
      if (!active) return [];
      return loadCloudExportJobs(currentSessionId, { silent });
    };

    refreshJobs(false);
    const timer = window.setInterval(async () => {
      const jobs = await refreshJobs(true);
      const hasActiveJob = jobs.some((job) => ['queued', 'processing'].includes(job?.status));
      if (!hasActiveJob) window.clearInterval(timer);
    }, 5000);

    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [currentSessionId]);

  useEffect(() => {
    if (!currentSessionId || !getBackendAuthToken()) {
      setShareLinks([]);
      setFeedbackComments([]);
      return;
    }
    loadShareLinks(currentSessionId, { silent: true });
    loadFeedbackComments(currentSessionId, { silent: true });
  }, [currentSessionId]);

  // Persist manualLatencyMs to localStorage — does NOT add delay to monitoring
  // It only offsets the recording start time for sync alignment
  useEffect(() => {
    localStorage.setItem('studio_latency_compensation_ms', String(manualLatencyMs));
    latencyCompensationMsRef.current = Number(manualLatencyMs) || 0;
    // This intentionally shifts clip placement only. Adding delay to monitoring would make latency worse.
  }, [manualLatencyMs]);

  // Auto-suggest latency range when Bluetooth device is detected
  useEffect(() => {
    if (isBluetoothDeviceRef?.current && manualLatencyMs === 0) {
      // Only auto-suggest if user hasn't set a value yet
      const savedMs = localStorage.getItem('studio_latency_compensation_ms');
      if (!savedMs || savedMs === '0') {
        setManualLatencyMs(150); // Default BT suggestion: 150ms
      }
    }
  }, []);

  useEffect(() => {
    if (!metronomeEnabled || (!isBeatPlaying && !isRecording)) {
      stopMetronomeClock();
      return;
    }

    startMetronomeClock(currentTime);
    return stopMetronomeClock;
  }, [metronomeEnabled, isBeatPlaying, isRecording, beatInfo?.bpm]);

  useEffect(() => {
    const previousSignature = mixPlaybackSignatureRef.current;
    mixPlaybackSignatureRef.current = mixPlaybackSignature;
    if (!previousSignature || previousSignature === mixPlaybackSignature) return;

    Object.entries(audioPlayersRef.current || {}).forEach(([playerKey, player]) => {
      const baseChannelKey = String(playerKey || '').split('::')[0];
      const channel = exportChannels.find((candidate) => candidate.channelKey === baseChannelKey);
      if (!channel || !player?.gain) return;

      const audible = isChannelAudibleInMix(channel);
      player.gain.gain.value = audible
        ? Math.max(0, Math.min(1, (channel?.volume ?? 1) * (channelFaderVolumesRef.current?.[channel.sectionId] ?? 1)))
        : 0;
      if (player?.panner) {
        player.panner.pan.value = Math.max(-1, Math.min(1, Number(channel?.pan) || 0));
      }
    });

    if (isBeatPlaying) {
      playTimelineClips(currentTime);
    }
  }, [mixPlaybackSignature]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const target = event.target;
      const isInteractiveTarget = Boolean(
        target?.closest?.('input, textarea, select, button, a, [contenteditable="true"], [role="textbox"]')
      );
      const key = event.key?.toLowerCase();
      if (key === 'escape') {
        event.preventDefault();
        if (countInRemaining != null) clearCountInTimers();
        setShowVersionHistory(false);
        setShowCloudSessionBrowser(false);
        setShowSaveDialog(false);
        return;
      }
      if (isInteractiveTarget || event.repeat) return;

      if ((event.metaKey || event.ctrlKey) && key === 'z') {
        event.preventDefault();
        if (event.shiftKey) handleRedo();
        else handleUndo();
      } else if ((event.metaKey || event.ctrlKey) && key === 'y') {
        event.preventDefault();
        handleRedo();
      } else if ((event.metaKey || event.ctrlKey) && key === 's') {
        event.preventDefault();
        if (!isRecording) handleSaveSession();
      } else if (key === ' ') {
        event.preventDefault();
        if (!isRecording) handleTransportPlay();
      } else if (key === 'r') {
        event.preventDefault();
        if (armedChannel || isRecording) handleTransportRecord();
      } else if (key === 's') {
        event.preventDefault();
        handleTransportStop();
      } else if (key === 'm') {
        event.preventDefault();
        setMetronomeEnabled((enabled) => !enabled);
      } else if (key === 'l') {
        event.preventDefault();
        if (!isRecording) handleToggleLoop();
      } else if (key === 'p') {
        event.preventDefault();
        if (!isRecording) handleTogglePunchIn();
      } else if (key === 'h') {
        event.preventDefault();
        if (!isRecording) handleOpenVersionHistory();
      } else if (key === 'c') {
        event.preventDefault();
        if (!isRecording) setCountInEnabled((enabled) => !enabled);
      } else if (key === 'x') {
        event.preventDefault();
        if (!isRecording) handleSplitTakeAtPlayhead();
      } else if (event.shiftKey && event.key === 'ArrowLeft') {
        event.preventDefault();
        if (!isRecording) handleNudgeSelectedClip(-1);
      } else if (event.shiftKey && event.key === 'ArrowRight') {
        event.preventDefault();
        if (!isRecording) handleNudgeSelectedClip(1);
      } else if (event.key === 'Backspace' || event.key === 'Delete') {
        event.preventDefault();
        if (!isRecording) handleDeleteSelectedClip();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handleTransportRewind();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleTransportFastForward();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [armedChannel, countInRemaining, isRecording, handleTransportPlay, handleTransportRecord, handleTransportStop, handleTransportRewind, handleTransportFastForward, handleUndo, handleRedo, handleSplitTakeAtPlayhead, handleNudgeSelectedClip, handleDeleteSelectedClip, handleToggleLoop, handleTogglePunchIn, handleOpenVersionHistory]);

  const handleTutorialComplete = () => {
    localStorage.setItem('studio_tutorial_seen', '1');
    setShowTutorial(false);
  };

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/sign-in');
    }
  }, [user, authLoading, navigate]);

  // Sync beat key notes to autotune DSP whenever beatInfo changes
  useEffect(() => {
    if (!pitchProcessorRef?.current) return;
    const notes = beatInfo?.keyInfo?.notes ?? null;
    updateAutotuneParams(pitchProcessorRef?.current, {
      beatKeyNotes: notes,
    });
  }, [beatInfo]);

  if (authLoading || (user && !subscriptionChecked)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center gap-3 text-foreground">
          <Icon name="Loader" size={24} className="animate-spin" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  // Show paywall if not subscribed
  if (!user || (subscriptionChecked && !hasSubscription)) {
    return <SubscriptionPaywall user={user} />;
  }

  return (
    <>
      <Helmet>
        <title>Recording Studio - makingitmixstudio</title>
        <meta name="description" content="Professional audio recording studio" />
      </Helmet>
      <Header />
      {showTutorial && <TutorialOverlay onComplete={handleTutorialComplete} />}
      {countInRemaining != null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="rounded-2xl border border-amber-500/50 bg-gray-950 px-8 py-6 text-center shadow-2xl shadow-amber-950/40">
            <p className="mb-2 text-[10px] font-mono uppercase tracking-[0.28em] text-amber-300">Count In</p>
            <div className="font-mono text-6xl font-black text-white">{Math.max(1, countInRemaining || 1)}</div>
            <button
              type="button"
              onClick={clearCountInTimers}
              className="mt-4 rounded border border-gray-700 px-3 py-1.5 text-xs text-gray-300 hover:border-amber-500/50 hover:text-amber-200"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {loadingSession && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="rounded-xl border border-gray-700 bg-gray-900 px-5 py-4 shadow-2xl">
            <div className="flex items-center gap-3 text-white">
              <Icon name="Loader" size={20} className="animate-spin" />
              <span className="text-sm font-medium">Loading cloud session...</span>
            </div>
          </div>
        </div>
      )}
      {showCloudSessionBrowser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4">
          <div className="w-full max-w-2xl rounded-xl border border-gray-700 bg-gray-900 shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-800 px-4 py-3">
              <div>
                <h3 className="text-sm font-semibold text-white">Load Cloud Project</h3>
                <p className="text-xs text-gray-500">Open a saved session from your Hetzner backend or import a local .mims file.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowCloudSessionBrowser(false)}
                className="rounded border border-gray-700 p-2 text-gray-400 hover:border-gray-500 hover:text-white"
                aria-label="Close cloud project browser"
              >
                <Icon name="X" size={14} />
              </button>
            </div>

            <div className="space-y-3 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={loadCloudSessions}
                  disabled={cloudSessionsLoading}
                  className="inline-flex items-center gap-2 rounded border border-gray-700 px-3 py-2 text-xs font-mono text-gray-300 hover:border-cyan-500/60 hover:text-cyan-200 disabled:opacity-40"
                >
                  <Icon name={cloudSessionsLoading ? 'Loader' : 'RefreshCw'} size={13} className={cloudSessionsLoading ? 'animate-spin' : ''} />
                  Refresh Projects
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCloudSessionBrowser(false);
                    sessionImportRef?.current?.click();
                  }}
                  className="inline-flex items-center gap-2 rounded border border-blue-500/40 px-3 py-2 text-xs font-mono text-blue-300 hover:bg-blue-950/30"
                >
                  <Icon name="Upload" size={13} />
                  Import .mims
                </button>
              </div>

              {cloudSessionError && (
                <div className="rounded border border-red-500/30 bg-red-950/20 px-3 py-2 text-xs text-red-200">
                  {cloudSessionError}
                </div>
              )}

              <div className="max-h-[420px] overflow-y-auto rounded border border-gray-800 bg-gray-950/40">
                {cloudSessionsLoading ? (
                  <div className="flex items-center gap-2 px-4 py-6 text-sm text-gray-400">
                    <Icon name="Loader" size={16} className="animate-spin" />
                    Loading cloud projects...
                  </div>
                ) : cloudSessions.length > 0 ? (
                  <div className="divide-y divide-gray-800">
                    {cloudSessions.map((session) => (
                      <div key={session?.id} className="flex items-center justify-between gap-3 px-4 py-3">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-gray-100">{session?.title || 'Untitled Session'}</div>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] font-mono uppercase text-gray-500">
                            <span>{session?.preset || 'hiphop'}</span>
                            {session?.updated_at && <span>Updated {new Date(session.updated_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleOpenCloudSession(session?.id)}
                          disabled={loadingSession}
                          className="shrink-0 rounded border border-cyan-500/40 bg-cyan-950/20 px-3 py-2 text-xs font-mono font-bold text-cyan-200 hover:bg-cyan-900/30 disabled:opacity-40"
                        >
                          Open
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-6 text-sm text-gray-500">
                    No cloud projects yet. Cloud Save this session first, or import a local .mims file.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {showVersionHistory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4">
          <div className="w-full max-w-2xl rounded-xl border border-gray-700 bg-gray-900 shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-800 px-4 py-3">
              <div>
                <h3 className="text-sm font-semibold text-white">Project History</h3>
                <p className="text-xs text-gray-500">Save restore points before risky edits, exports, or collaborator changes.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowVersionHistory(false)}
                className="rounded border border-gray-700 p-2 text-gray-400 hover:border-gray-500 hover:text-white"
                aria-label="Close project history"
              >
                <Icon name="X" size={14} />
              </button>
            </div>

            <div className="space-y-3 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={handleCreateProjectVersion}
                  disabled={Boolean(versionBusyId)}
                  className="inline-flex items-center gap-2 rounded border border-amber-500/40 bg-amber-950/20 px-3 py-2 text-xs font-mono font-bold text-amber-200 hover:bg-amber-900/30 disabled:opacity-40"
                >
                  <Icon name={versionBusyId === 'create' ? 'Loader' : 'Plus'} size={13} className={versionBusyId === 'create' ? 'animate-spin' : ''} />
                  Save Snapshot
                </button>
                <button
                  type="button"
                  onClick={() => refreshSessionVersions()}
                  disabled={versionsLoading}
                  className="inline-flex items-center gap-2 rounded border border-gray-700 px-3 py-2 text-xs font-mono text-gray-300 hover:border-amber-500/50 hover:text-amber-200 disabled:opacity-40"
                >
                  <Icon name={versionsLoading ? 'Loader' : 'RefreshCw'} size={13} className={versionsLoading ? 'animate-spin' : ''} />
                  Refresh
                </button>
              </div>

              <div className="max-h-[420px] overflow-y-auto rounded border border-gray-800 bg-gray-950/40">
                {versionsLoading ? (
                  <div className="flex items-center gap-2 px-4 py-6 text-sm text-gray-400">
                    <Icon name="Loader" size={16} className="animate-spin" />
                    Loading project history...
                  </div>
                ) : sessionVersions.length > 0 ? (
                  <div className="divide-y divide-gray-800">
                    {sessionVersions.map((version) => {
                      const versionDate = version?.createdAt || version?.savedAt;
                      const takeCount = Object.values(version?.sessionData?.takes || {}).reduce((count, items) => count + (items?.length || 0), 0);
                      return (
                        <div key={version.id} className="flex items-center justify-between gap-3 px-4 py-3">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-gray-100">{version?.sessionTitle || sessionTitle || 'Untitled Session'}</div>
                            <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] font-mono uppercase text-gray-500">
                              {versionDate && <span>{new Date(versionDate).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>}
                              <span>{version?.sessionData?.beatInfo?.name || 'No beat'}</span>
                              <span>{takeCount} takes</span>
                            </div>
                          </div>
                          <div className="flex shrink-0 gap-2">
                            <button
                              type="button"
                              onClick={() => handleRestoreProjectVersion(version)}
                              disabled={Boolean(versionBusyId)}
                              className="rounded border border-cyan-500/40 bg-cyan-950/20 px-3 py-2 text-xs font-mono font-bold text-cyan-200 hover:bg-cyan-900/30 disabled:opacity-40"
                            >
                              {versionBusyId === version.id ? 'Restoring' : 'Restore'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteProjectVersion(version)}
                              disabled={Boolean(versionBusyId)}
                              className="rounded border border-red-500/30 px-3 py-2 text-xs font-mono text-red-300 hover:bg-red-950/30 disabled:opacity-40"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="px-4 py-6 text-sm text-gray-500">
                    No snapshots yet. Save one before you edit, comp, mix, or export.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="min-h-screen bg-background pt-[60px]">
        <div className="container-studio py-4 lg:py-6">

          {/* Page header */}
          <div className="mb-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <h1 className="text-h2 font-heading mb-0.5">Recording Studio</h1>
                <p className="text-muted-foreground text-xs">Record your vocals over your beat</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <AutoSaveIndicator
                  sessionData={autoSaveData}
                  sessionId={currentSessionId}
                  onAutoSave={handleAutoSave}
                />
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={handleDeleteSelectedClip}
                    disabled={isRecording || !Object.keys(takes || {}).length}
                    title="Delete selected clip"
                    className="h-9 w-9 flex items-center justify-center rounded border border-gray-700 bg-gray-900 text-gray-300 hover:border-red-500/60 hover:bg-gray-800 hover:text-red-300 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Icon name="Trash2" size={15} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleNudgeSelectedClip(-1)}
                    disabled={isRecording || !Object.keys(takes || {}).length}
                    title="Nudge selected clip left"
                    className="h-9 w-9 flex items-center justify-center rounded border border-gray-700 bg-gray-900 text-gray-300 hover:border-cyan-500/60 hover:bg-gray-800 hover:text-cyan-300 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Icon name="MoveLeft" size={15} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleNudgeSelectedClip(1)}
                    disabled={isRecording || !Object.keys(takes || {}).length}
                    title="Nudge selected clip right"
                    className="h-9 w-9 flex items-center justify-center rounded border border-gray-700 bg-gray-900 text-gray-300 hover:border-cyan-500/60 hover:bg-gray-800 hover:text-cyan-300 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Icon name="MoveRight" size={15} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSplitTakeAtPlayhead()}
                    disabled={isRecording || !Object.keys(takes || {}).length}
                    title="Split selected clip at playhead"
                    className="h-9 w-9 flex items-center justify-center rounded border border-gray-700 bg-gray-900 text-gray-300 hover:border-emerald-500/60 hover:bg-gray-800 hover:text-emerald-300 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Icon name="Scissors" size={15} />
                  </button>
                  <button
                    type="button"
                    onClick={handleUndo}
                    disabled={!undoStack?.length || isRecording}
                    title="Undo"
                    className="h-9 w-9 flex items-center justify-center rounded border border-gray-700 bg-gray-900 text-gray-300 hover:border-gray-500 hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Icon name="Undo2" size={15} />
                  </button>
                  <button
                    type="button"
                    onClick={handleRedo}
                    disabled={!redoStack?.length || isRecording}
                    title="Redo"
                    className="h-9 w-9 flex items-center justify-center rounded border border-gray-700 bg-gray-900 text-gray-300 hover:border-gray-500 hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Icon name="Redo2" size={15} />
                  </button>
                </div>
                {/* Hidden file input for session import */}
                <input
                  ref={sessionImportRef}
                  type="file"
                  accept=".mims,application/json"
                  className="hidden"
                  onChange={handleImportFileChange}
                />
                <Button variant="outline" size="sm" onClick={handleImportSession} iconName="Upload" iconPosition="left" className="hidden sm:flex border-blue-500/50 text-blue-400 hover:bg-blue-900/30">
                  Load Session
                </Button>
                <Button variant="outline" size="sm" onClick={handleExportSession} iconName="Download" iconPosition="left" className="hidden sm:flex border-green-500/50 text-green-400 hover:bg-green-900/30">
                  Save Session
                </Button>
                <Button variant="outline" size="sm" onClick={handleOpenVersionHistory} iconName="History" iconPosition="left" className="hidden sm:flex border-amber-500/50 text-amber-300 hover:bg-amber-900/25">
                  History
                </Button>
                <Button variant="outline" size="sm" onClick={handleSaveSession} iconName="Save" iconPosition="left" disabled={saving} className="hidden sm:flex">
                  {saving ? 'Saving...' : 'Cloud Save'}
                </Button>
                <Button variant="outline" size="sm" onClick={handleShareSession} iconName="Share2" iconPosition="left" disabled={shareSaving || saving} className="hidden sm:flex border-cyan-500/50 text-cyan-300 hover:bg-cyan-900/30">
                  {shareSaving ? 'Sharing...' : 'Share for Feedback'}
                </Button>
                <button
                  type="button"
                  onClick={() => setShowTutorial(true)}
                  title="Open studio guide"
                  className="hidden h-9 w-9 items-center justify-center rounded border border-gray-700 bg-gray-900 text-gray-300 hover:border-purple-500/60 hover:bg-gray-800 hover:text-purple-200 sm:flex"
                  aria-label="Open studio guide"
                >
                  <Icon name="CircleHelp" size={15} />
                </button>
                <Button variant="default" onClick={handleProceedToMix} iconName="ArrowRight" iconPosition="right" className="hidden sm:flex bg-purple-600 hover:bg-purple-500 text-white font-bold px-5 py-2.5 text-base shadow-lg shadow-purple-900/40 border-2 border-purple-400/30">
                  Mix
                </Button>
              </div>
            </div>
          </div>

          {/* Transport */}
          <div className="mb-4">
            <ProToolsTransport
              isPlaying={isBeatPlaying}
              isRecording={isRecording}
              isPunchInEnabled={isPunchInEnabled}
              isLoopEnabled={isLoopEnabled}
              isMetronomeEnabled={metronomeEnabled}
              currentTime={currentTime}
              beatDuration={beatInfo?.durationInSeconds || 180}
              bpm={beatInfo?.bpm || 120}
              onPlay={handleTransportPlay}
              onStop={handleTransportStop}
              onRecord={handleTransportRecord}
              onRewind={handleTransportRewind}
              onFastForward={handleTransportFastForward}
              onTogglePunchIn={handleTogglePunchIn}
              onToggleLoop={handleToggleLoop}
              onToggleMetronome={() => setMetronomeEnabled(p => !p)}
              disabled={!beatInfo && !allChannelsFlat?.some(c => c?.recorded)}
              activeChannel={armedChannel}
            />
          </div>

          {(shareLinks.length > 0 || currentSessionId) && (
            <div className="mb-4 rounded-lg border border-cyan-500/20 bg-cyan-950/10 p-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <div className="flex h-9 w-9 items-center justify-center rounded border border-cyan-500/30 bg-cyan-900/30 text-cyan-200">
                    <Icon name="MessagesSquare" size={16} />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-cyan-100">Feedback Links</h4>
                    <p className="text-[10px] text-cyan-200/60">Copy or revoke collaborator comment links</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => loadShareLinks(currentSessionId)}
                    disabled={!currentSessionId || sharesLoading}
                    className="flex items-center gap-1.5 rounded border border-cyan-500/30 px-2.5 py-1.5 text-xs font-mono text-cyan-200 hover:bg-cyan-500/10 disabled:opacity-40"
                  >
                    <Icon name={sharesLoading ? 'Loader' : 'RefreshCw'} size={12} className={sharesLoading ? 'animate-spin' : ''} />
                    Refresh
                  </button>
                  <button
                    type="button"
                    onClick={handleShareSession}
                    disabled={shareSaving || saving}
                    className="flex items-center gap-1.5 rounded border border-cyan-400/50 bg-cyan-900/30 px-2.5 py-1.5 text-xs font-mono font-bold text-cyan-100 hover:bg-cyan-800/40 disabled:opacity-40"
                  >
                    <Icon name={shareSaving ? 'Loader' : 'Share2'} size={12} className={shareSaving ? 'animate-spin' : ''} />
                    New Link
                  </button>
                </div>
              </div>
              {shareLinks.length > 0 ? (
                <div className="mt-3 grid gap-2 md:grid-cols-2">
                  {shareLinks.slice(0, 4).map((link) => {
                    const revoked = Boolean(link?.revoked_at);
                    const expired = link?.expires_at && new Date(link.expires_at).getTime() < Date.now();
                    const inactive = revoked || expired;
                    return (
                      <div key={link?.id || link?.token} className={`rounded border p-2 ${inactive ? 'border-gray-700 bg-gray-950/40' : 'border-cyan-500/20 bg-gray-950/40'}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate text-[10px] font-mono uppercase text-gray-300">
                            {link?.permission === 'comment' ? 'Comment Link' : 'View Link'}
                          </span>
                          <span className={`rounded border px-1.5 py-0.5 text-[9px] font-mono uppercase ${inactive ? 'border-gray-600 text-gray-400' : 'border-green-500/40 text-green-300'}`}>
                            {revoked ? 'Revoked' : expired ? 'Expired' : 'Active'}
                          </span>
                        </div>
                        <div className="mt-1 flex items-center justify-between gap-2 text-[10px] text-gray-500">
                          <span>{Number(link?.comment_count || 0)} comments</span>
                          {link?.expires_at && <span>Expires {new Date(link.expires_at).toLocaleDateString()}</span>}
                        </div>
                        <div className="mt-2 flex gap-1.5">
                          <button
                            type="button"
                            onClick={() => handleCopyShareLink(link)}
                            disabled={!link?.share_url || inactive}
                            className="flex flex-1 items-center justify-center gap-1 rounded border border-gray-700 px-2 py-1 text-[10px] font-mono text-gray-300 hover:border-cyan-500/50 hover:text-cyan-200 disabled:opacity-40"
                          >
                            <Icon name="Copy" size={10} />
                            Copy
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRevokeShareLink(link)}
                            disabled={inactive || shareRevokingId === link?.id}
                            className="flex flex-1 items-center justify-center gap-1 rounded border border-red-500/30 px-2 py-1 text-[10px] font-mono text-red-300 hover:bg-red-950/30 disabled:opacity-40"
                          >
                            <Icon name={shareRevokingId === link?.id ? 'Loader' : 'Ban'} size={10} className={shareRevokingId === link?.id ? 'animate-spin' : ''} />
                            Revoke
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="mt-3 rounded border border-gray-800 bg-gray-950/40 px-3 py-2 text-xs text-gray-400">
                  No feedback links yet. Create one to collect timestamped comments from collaborators.
                </p>
              )}
              <div className="mt-3 rounded border border-cyan-500/15 bg-gray-950/45 p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <div>
                    <h5 className="text-xs font-semibold uppercase tracking-[0.16em] text-cyan-100">Received Feedback</h5>
                    <p className="text-[10px] text-cyan-200/50">Collaborator comments from active and past feedback links</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => loadFeedbackComments(currentSessionId)}
                    disabled={!currentSessionId || feedbackLoading}
                    className="flex items-center gap-1.5 rounded border border-cyan-500/30 px-2 py-1 text-[10px] font-mono text-cyan-200 hover:bg-cyan-500/10 disabled:opacity-40"
                  >
                    <Icon name={feedbackLoading ? 'Loader' : 'RefreshCw'} size={11} className={feedbackLoading ? 'animate-spin' : ''} />
                    Refresh
                  </button>
                </div>
                {feedbackComments.length > 0 ? (
                  <div className="space-y-2">
                    {feedbackComments.slice(0, 5).map((comment) => {
                      const hasTimestamp = comment?.timestamp_seconds !== null && comment?.timestamp_seconds !== undefined;
                      return (
                        <div key={comment?.id} className="rounded border border-gray-800 bg-black/25 p-2">
                          <div className="flex flex-wrap items-center justify-between gap-2 text-[10px] text-gray-500">
                            <span className="font-mono uppercase text-gray-300">{comment?.author_name || 'Collaborator'}</span>
                            <span>{comment?.created_at ? new Date(comment.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'New feedback'}</span>
                          </div>
                          <p className="mt-1 text-xs leading-relaxed text-gray-200">{comment?.body}</p>
                          {hasTimestamp && (
                            <button
                              type="button"
                              onClick={() => handleJumpToFeedbackTimestamp(comment.timestamp_seconds)}
                              className="mt-2 inline-flex items-center gap-1 rounded border border-cyan-500/25 px-2 py-1 text-[10px] font-mono text-cyan-200 hover:bg-cyan-500/10"
                            >
                              <Icon name="LocateFixed" size={10} />
                              Jump to {formatTimelineLabel(comment.timestamp_seconds)}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="rounded border border-gray-800 bg-black/20 px-3 py-2 text-xs text-gray-500">
                    No collaborator comments yet.
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
            {/* Left/Main column */}
            <div className="lg:col-span-2 space-y-4">
              <BeatImporter
                ref={beatImporterRef}
                onBeatImport={handleBeatImport}
                beatInfo={beatInfo}
                isPlaying={isBeatPlaying}
                onPlayPause={handleTransportPlay}
                onAudioReady={handleAudioReady}
                onTimeUpdate={handleBeatTimeUpdate}
                seekTime={seekTime}
              />

              {/* Punch-in banner */}
              {isPunchInEnabled && (
                <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg bg-orange-900/20 border border-orange-500/40">
                  <Icon name="Crosshair" size={16} color="#f97316" />
                  <div className="flex-1">
                    <p className="text-sm text-orange-300">
                      <strong>Punch-In Mode Active</strong>
                      {punchInPoint != null && <span className="ml-2 font-mono text-xs">IN: {formatTimelineLabel(punchInPoint)}</span>}
                      {punchOutPoint != null && <span className="ml-2 font-mono text-xs">OUT: {formatTimelineLabel(punchOutPoint)}</span>}
                    </p>
                    <div className="mt-2 grid grid-cols-2 gap-2 max-w-sm">
                      <label className="text-[10px] uppercase tracking-[0.18em] text-orange-200/70">
                        In
                        <input
                          type="number"
                          min="0"
                          step="0.25"
                          value={punchInPoint ?? 0}
                          onChange={(event) => updatePunchPoint('in', event.target.value)}
                          className="mt-1 w-full rounded border border-orange-500/30 bg-black/40 px-2 py-1 text-xs font-mono text-orange-100 outline-none focus:border-orange-400"
                        />
                      </label>
                      <label className="text-[10px] uppercase tracking-[0.18em] text-orange-200/70">
                        Out
                        <input
                          type="number"
                          min="0"
                          step="0.25"
                          value={punchOutPoint ?? Math.min(getProjectDuration(), (punchInPoint ?? currentTime) + 8)}
                          onChange={(event) => updatePunchPoint('out', event.target.value)}
                          className="mt-1 w-full rounded border border-orange-500/30 bg-black/40 px-2 py-1 text-xs font-mono text-orange-100 outline-none focus:border-orange-400"
                        />
                      </label>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => updatePunchPoint('in', currentTime)} className="rounded border border-orange-500/40 px-2 py-1 text-xs text-orange-300 hover:bg-orange-500/10">Set In</button>
                    <button onClick={() => updatePunchPoint('out', currentTime)} className="rounded border border-orange-500/40 px-2 py-1 text-xs text-orange-300 hover:bg-orange-500/10">Set Out</button>
                    <button onClick={() => { setPunchInPoint(null); setPunchOutPoint(null); }} className="rounded border border-orange-500/20 px-2 py-1 text-xs text-orange-400 hover:text-orange-300">Clear</button>
                  </div>
                </div>
              )}

              {/* Loop banner */}
              {isLoopEnabled && (
                <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg bg-blue-900/20 border border-blue-500/40">
                  <Icon name="Repeat" size={16} color="#60a5fa" />
                  <div className="flex-1">
                    <p className="text-sm text-blue-300">
                      <strong>Loop Mode Active</strong>
                      <span className="ml-2 font-mono text-xs">{formatTimelineLabel(getLoopRange().start)} - {formatTimelineLabel(getLoopRange().end)}</span>
                    </p>
                    <div className="mt-2 grid grid-cols-2 gap-2 max-w-sm">
                      <label className="text-[10px] uppercase tracking-[0.18em] text-blue-200/70">
                        Start
                        <input
                          type="number"
                          min="0"
                          step="0.25"
                          value={loopStartPoint}
                          onChange={(event) => updateLoopPoint('start', event.target.value)}
                          className="mt-1 w-full rounded border border-blue-500/30 bg-black/40 px-2 py-1 text-xs font-mono text-blue-100 outline-none focus:border-blue-400"
                        />
                      </label>
                      <label className="text-[10px] uppercase tracking-[0.18em] text-blue-200/70">
                        End
                        <input
                          type="number"
                          min="0.25"
                          step="0.25"
                          value={loopEndPoint}
                          onChange={(event) => updateLoopPoint('end', event.target.value)}
                          className="mt-1 w-full rounded border border-blue-500/30 bg-black/40 px-2 py-1 text-xs font-mono text-blue-100 outline-none focus:border-blue-400"
                        />
                      </label>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => updateLoopPoint('start', currentTime)} className="rounded border border-blue-500/40 px-2 py-1 text-xs text-blue-300 hover:bg-blue-500/10">Set Start</button>
                    <button onClick={() => updateLoopPoint('end', currentTime)} className="rounded border border-blue-500/40 px-2 py-1 text-xs text-blue-300 hover:bg-blue-500/10">Set End</button>
                  </div>
                </div>
              )}

              {/* Arm instruction */}
              {!armedChannel && beatInfo && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-accent/10 border border-accent/20">
                  <Icon name="Info" size={16} color="var(--color-accent)" />
                  <p className="text-sm text-accent/90">Click the <strong>R</strong> button on a track to arm it, then press Record.</p>
                </div>
              )}

              {/* Live Monitor toggle — above tracks */}
              <div className="p-3 rounded-lg bg-card border border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-12 h-12 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0 ${
                      monitoringEnabled ? 'bg-green-900/40' : 'bg-gray-800'
                    }`}>
                      <Icon name="Headphones" size={24} color={monitoringEnabled ? '#4ade80' : '#6b7280'} />
                    </div>
                    <div>
                      <h4 className="text-sm font-medium">Live Monitor</h4>
                      <p className="text-[10px] text-muted-foreground">
                        {monitoringEnabled ? 'Hearing your voice in headphones' : 'Off'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleMonitoringToggle}
                    type="button"
                    title={monitoringEnabled ? 'Turn live monitor off' : 'Turn live monitor on'}
                    aria-label={monitoringEnabled ? 'Turn live monitor off' : 'Turn live monitor on'}
                    className={`relative w-12 h-6 rounded-full transition-all ${
                      monitoringEnabled ? 'bg-green-600' : 'bg-gray-700'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-all ${
                        monitoringEnabled ? 'translate-x-6' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div className="p-3 rounded-lg bg-card border border-border">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <div className="w-12 h-12 rounded-full bg-cyan-900/30 flex items-center justify-center flex-shrink-0">
                      <Icon name="Clock3" size={22} color="#67e8f9" />
                    </div>
                    <div>
                      <h4 className="text-sm font-medium">Recording Sync</h4>
                      <p className="text-[10px] text-muted-foreground">
                        {manualLatencyMs === 0
                          ? 'Places new takes at the record start'
                          : `Places new takes ${manualLatencyMs > 0 ? 'earlier' : 'later'} by ${Math.abs(manualLatencyMs)} ms`}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowLatencyFader((visible) => !visible)}
                    disabled={isRecording}
                    className="rounded border border-cyan-500/30 px-2.5 py-1 text-xs font-mono text-cyan-200 hover:bg-cyan-500/10 disabled:opacity-40"
                  >
                    {showLatencyFader ? 'Hide' : 'Adjust'}
                  </button>
                </div>
                {showLatencyFader && (
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center gap-3">
                      <span className="w-10 text-right text-[10px] font-mono text-gray-500">Late</span>
                      <input
                        type="range"
                        min="-200"
                        max="500"
                        step="5"
                        value={manualLatencyMs}
                        onChange={(event) => setManualLatencyMs(Number(event.target.value))}
                        disabled={isRecording}
                        className="flex-1 accent-cyan-300"
                      />
                      <span className="w-12 text-[10px] font-mono text-gray-500">Early</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-mono text-cyan-200">{manualLatencyMs} ms</span>
                      <button
                        type="button"
                        onClick={() => setManualLatencyMs(0)}
                        disabled={isRecording || manualLatencyMs === 0}
                        className="rounded border border-gray-700 px-2 py-1 text-[10px] font-mono text-gray-400 hover:border-cyan-500/50 hover:text-cyan-200 disabled:opacity-40"
                      >
                        Reset
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Count-in / pre-roll */}
              <div className="p-3 rounded-lg bg-card border border-border">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                      countInEnabled ? 'bg-amber-900/40' : 'bg-gray-800'
                    }`}>
                      <Icon name="Timer" size={23} color={countInEnabled ? '#fbbf24' : '#6b7280'} />
                    </div>
                    <div>
                      <h4 className="text-sm font-medium">Count-In</h4>
                      <p className="text-[10px] text-muted-foreground">
                        {countInEnabled ? `${countInBars} bar pre-roll before recording` : 'Starts recording immediately'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={countInBars}
                      onChange={(event) => setCountInBars(Number(event.target.value))}
                      disabled={!countInEnabled || isRecording}
                      className="h-8 rounded border border-gray-700 bg-gray-900 px-2 text-xs text-gray-200 outline-none disabled:opacity-40"
                    >
                      <option value={1}>1 bar</option>
                      <option value={2}>2 bars</option>
                    </select>
                    <button
                      onClick={() => setCountInEnabled((enabled) => !enabled)}
                      disabled={isRecording || countInRemaining != null}
                      className={`relative w-12 h-6 rounded-full transition-all disabled:opacity-40 ${
                        countInEnabled ? 'bg-amber-500' : 'bg-gray-700'
                      }`}
                      title="Toggle count-in"
                      aria-label={countInEnabled ? 'Disable count-in' : 'Enable count-in'}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-all ${
                          countInEnabled ? 'translate-x-6' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              <WaveformZoom
                zoom={waveformZoom}
                onZoomChange={setWaveformZoom}
                snapEnabled={snapEnabled}
                onSnapToggle={() => setSnapEnabled((enabled) => !enabled)}
                gridSize={gridSize}
                onGridSizeChange={setGridSize}
              />

              {/* Pro Tools Edit Window */}
              <ProToolsEditWindow
                beatInfo={beatInfo}
                isBeatPlaying={isBeatPlaying}
                onBeatPlayPause={handleTransportPlay}
                sections={editWindowSections}
                armedChannel={armedChannel}
                auditionChannels={auditionChannels}
                onArm={handleArm}
                onMute={handleMuteToggle}
                onSolo={handleSoloToggle}
                onAudition={handleAudition}
                onPlayback={handlePlayback}
                onDelete={handleDelete}
                onPanChange={handlePanChange}
                onVolumeChange={handleTrackVolumeChange}
                onClipChange={handleUpdateChannelClip}
                onSelectTake={handleSelectTake}
                onSeek={(time) => seekTimeline(time, { restartPlayback: true })}
                onBeginEdit={handleBeginEditHistory}
                recordedWaveforms={recordedWaveforms}
                takes={takes}
                timelineDuration={Math.max(beatInfo?.durationInSeconds || 180, 180)}
                currentTime={currentTime}
                snapEnabled={snapEnabled}
                snapSeconds={snapSeconds}
                disabled={isRecording}
              />

              {/* Take Manager */}
              {Object.keys(takes)?.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-xs font-mono text-gray-500 uppercase tracking-wider px-1">Takes</h4>
                  {Object.entries(takes)?.map(([key, takesArr]) => (
                    <TakeManager
                      key={key}
                      takes={takesArr}
                      channelKey={key}
                      onSelectTake={handleSelectTake}
                      onDeleteTake={handleDeleteTake}
                      onRenameTake={handleRenameTake}
                      onUpdateTakeClip={handleUpdateTakeClip}
                      onDuplicateTake={handleDuplicateTake}
                      onPromoteTakeToComp={handlePromoteTakeToComp}
                      onToggleTakeTimelineActive={handleToggleTakeTimelineActive}
                      onSplitTake={handleSplitTakeAtPlayhead}
                      onResetTakeClip={handleResetTakeClip}
                      onBeginEdit={handleBeginEditHistory}
                      disabled={isRecording}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Right panel */}
            <div className="space-y-4">
              {/* Right panel tabs */}
              <div className="flex gap-1 bg-gray-900 border border-gray-700 rounded-lg p-1">
                {rightPanelTabs?.map(tab => (
                  <button
                    key={tab?.id}
                    onClick={() => setRightPanel(tab?.id)}
                    className={`flex-1 flex flex-col items-center gap-0.5 py-1.5 rounded text-[9px] font-mono transition-all ${
                      rightPanel === tab?.id
                        ? 'bg-gray-700 text-white' : 'text-gray-500 hover:text-gray-300'
                    }`}
                  >
                    <Icon name={tab?.icon} size={12} />
                    {tab?.label}
                  </button>
                ))}
              </div>

              {rightPanel === 'controls' && (
                <>
                  {/* BandLab-style simple mode: hide ChannelFaders + ProjectTemplates by default */}
                  <div className="flex items-center justify-between mb-2">
                    <button
                      onClick={() => setSimpleMode(v => !v)}
                      className="text-[10px] font-mono px-2 py-1 rounded bg-muted text-muted-foreground hover:bg-accent/10 hover:text-accent transition-all"
                    >
                      {simpleMode ? "+ Show advanced" : "− Hide advanced"}
                    </button>
                  </div>
                  {!simpleMode && (<>
                    <ChannelFaders
                      volumes={channelFaderVolumes}
                      onVolumeChange={handleFaderVolumeChange}
                    />
                    <ProjectTemplates onApplyTemplate={handleApplyTemplate} selectedTemplateId={selectedTemplateId} />
                  </>)}
                  <PresetSelector selectedPreset={selectedPreset} onPresetChange={(preset) => {
                    setSelectedPreset(preset);
                    // Clear template selection when manually picking a vocal preset
                    setSelectedTemplateId(null);
                    // Auto-enable autotune when melodic is selected, disable for others
                    if (preset === 'melodic') {
                      setAutotuneEnabled(true);
                    } else {
                      setAutotuneEnabled(false);
                    }
                  }} />
                  <VocalFxChain
                    settings={vocalFxSettings}
                    onChange={handleVocalFxChange}
                    disabled={isRecording}
                  />

                  {/* Autotune — only show when Melodic preset is selected */}
                  {selectedPreset === 'melodic' && (
                    <AutotuneControls
                      enabled={autotuneEnabled}
                      retuneSpeed={retuneSpeed}
                      onToggle={() => setAutotuneEnabled(v => !v)}
                      onSpeedChange={setRetuneSpeed}
                      disabled={isRecording}
                      beatKey={beatInfo?.key}
                      intensity={autotuneIntensity}
                      onIntensityChange={setAutotuneIntensity}
                      wetMix={autotuneWetMix}
                      onWetMixChange={setAutotuneWetMix}
                      onLoadPreset={(preset) => {
                        if (preset?.intensity != null) setAutotuneIntensity(preset?.intensity);
                        if (preset?.wetMix != null) setAutotuneWetMix(preset?.wetMix);
                        if (preset?.retuneSpeed != null) setRetuneSpeed(preset?.retuneSpeed);
                        if (preset?.beatVolume != null) {
                          const vol = Math.max(0, Math.min(100, preset?.beatVolume));
                          setBeatVolumeState(vol);
                          beatImporterRef?.current?.setVolume(vol / 100);
                        }
                      }}
                    />
                  )}

                  <DemoCounter />
                  <DeviceSelector onDeviceChange={handleDeviceChange} disabled={isRecording} />
                  <MicReadinessPanel
                    selectedDevice={selectedDevice}
                    beatLoaded={Boolean(beatInfo)}
                    armedChannel={armedChannel ? `${armedChannel.sectionId}-${armedChannel.channelId}` : null}
                    inputLevel={inputLevel}
                    monitoringEnabled={monitoringEnabled}
                    manualLatencyMs={manualLatencyMs}
                    onLatencyChange={setManualLatencyMs}
                    disabled={isRecording}
                  />
                  <MetronomeControl enabled={metronomeEnabled} bpm={beatInfo?.bpm || 120} onToggle={() => setMetronomeEnabled(p => !p)} disabled={isRecording} />
                  <AudioMeter isRecording={isRecording} isArmed={!!armedChannel} level={inputLevel} />
                </>
              )}

              {rightPanel === 'mixer' && (
                <div className="space-y-3">
                  <TrackMixer channels={verse1Channels} sectionTitle="Verse 1" onChannelUpdate={handleMixerChannelUpdate('Verse 1')} disabled={!beatInfo} />
                  <TrackMixer channels={verse2Channels} sectionTitle="Verse 2" onChannelUpdate={handleMixerChannelUpdate('Verse 2')} disabled={!beatInfo} />
                  <TrackMixer channels={verse3Channels} sectionTitle="Verse 3" onChannelUpdate={handleMixerChannelUpdate('Verse 3')} disabled={!beatInfo} />
                  <TrackMixer channels={hookChannels} sectionTitle="Hook" onChannelUpdate={handleMixerChannelUpdate('Hook')} disabled={!beatInfo} />
                </div>
              )}

              {rightPanel === 'export' && (
                <ExportPanel
                  beatInfo={beatInfo}
                  channels={allChannelsFlat?.filter(c => c?.recorded)}
                  disabled={!beatInfo && !allChannelsFlat?.some(c => c?.recorded)}
                  onBounce={handleBounce}
                  onQueueCloudExport={handleQueueCloudExport}
                  cloudExportJobs={cloudExportJobs}
                  cloudExportsLoading={cloudExportsLoading}
                  onRefreshCloudExports={() => loadCloudExportJobs(currentSessionId)}
                  onCancelCloudExport={handleCancelCloudExport}
                />
              )}

              {rightPanel === 'notes' && (
                <div className="space-y-3">
                  <TimeMarkers
                    currentTime={currentTime}
                    duration={getProjectDuration()}
                    markers={timeMarkers}
                    onSeek={handleSeekToMarker}
                    onMarkersChange={handleTimeMarkersChange}
                  />
                  <SessionNotes
                    sessionId={currentSessionId}
                    notes={sessionNotes}
                    lyrics={sessionLyrics}
                    onNotesChange={setSessionNotes}
                    onLyricsChange={setSessionLyrics}
                  />
                </div>
              )}

              {autoMixing && (
                <div className="p-4 rounded-lg bg-accent/10 border border-accent/20 space-y-3">
                  <div className="flex items-start gap-3">
                    <Icon name="Loader" size={20} color="var(--color-accent)" className="animate-spin" />
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-accent mb-1">Auto-Mixing</h4>
                      <p className="text-xs text-accent/80">Processing your recording...</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Mobile bottom actions */}
          <div className="lg:hidden mt-4 grid grid-cols-2 gap-2 pb-4">
            <Button variant="outline" size="sm" onClick={handleImportSession} iconName="Upload" iconPosition="left" className="w-full border-blue-500/50 text-blue-400 hover:bg-blue-900/30">
              Load Session
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportSession} iconName="Download" iconPosition="left" className="w-full border-green-500/50 text-green-400 hover:bg-green-900/30">
              Save Session
            </Button>
            <Button variant="outline" size="sm" onClick={handleOpenVersionHistory} iconName="History" iconPosition="left" className="w-full border-amber-500/50 text-amber-300 hover:bg-amber-900/25">
              History
            </Button>
            <Button variant="outline" size="sm" onClick={handleSaveSession} disabled={saving} className="w-full bg-gray-700 hover:bg-gray-600 text-white">
              {saving ? <span className="flex items-center gap-2"><Icon name="Loader" size={16} className="animate-spin" />Saving...</span> : 'Cloud Save'}
            </Button>
            <Button variant="outline" size="sm" onClick={handleShareSession} disabled={shareSaving || saving} iconName="Share2" iconPosition="left" className="w-full border-cyan-500/50 text-cyan-300 hover:bg-cyan-900/30">
              {shareSaving ? 'Sharing...' : 'Share for Feedback'}
            </Button>
            <Button variant="default" onClick={handleProceedToMix} iconName="ArrowRight" iconPosition="right" className="w-full bg-purple-600 hover:bg-purple-500 text-white font-bold text-base py-3 shadow-lg shadow-purple-900/40 border-2 border-purple-400/30">
              Proceed to Mix
            </Button>
          </div>
        </div>
      </div>
      {/* Error modal */}
      {(micPermissionError || recordingError) && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-6 max-w-md w-full border border-gray-700">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-error/20 flex items-center justify-center flex-shrink-0">
                <Icon name="AlertTriangle" size={24} color="var(--color-error)" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-white mb-2">{micPermissionError ? 'Microphone Error' : 'Recording Error'}</h3>
                <p className="text-sm text-gray-300 leading-relaxed">{micPermissionError || recordingError}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button onClick={() => { setMicPermissionError(null); setRecordingError(null); }} disabled={saving} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white">Dismiss</Button>
              {micPermissionError && (
                <Button onClick={() => { setMicPermissionError(null); setRecordingError(null); initializeAudioContext(true); }} className="flex-1 bg-accent hover:bg-accent/90 text-white">Retry Microphone</Button>
              )}
            </div>
          </div>
        </div>
      )}
      {saveNotice && (
        <div className="fixed bottom-5 right-5 z-50 max-w-sm rounded-lg border border-gray-700 bg-gray-900/95 px-4 py-3 shadow-2xl">
          <div className="flex items-start gap-3">
            <Icon
              name={saveNotice.type === 'error' ? 'AlertTriangle' : 'CheckCircle2'}
              size={18}
              color={saveNotice.type === 'error' ? '#f87171' : '#34d399'}
            />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white">
                {saveNotice.type === 'error' ? 'Save failed' : 'Session saved'}
              </p>
              <p className="mt-0.5 text-xs leading-relaxed text-gray-300">{saveNotice.message}</p>
            </div>
            <button
              type="button"
              onClick={() => setSaveNotice(null)}
              className="ml-1 text-gray-500 hover:text-gray-200"
              aria-label="Dismiss save status"
            >
              <Icon name="X" size={14} />
            </button>
          </div>
        </div>
      )}
      {recoverySnapshot && (
        <div className="fixed inset-x-0 top-4 z-50 mx-auto w-[calc(100%-2rem)] max-w-2xl rounded-lg border border-cyan-500/40 bg-gray-950/95 p-4 shadow-2xl backdrop-blur">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded border border-cyan-500/40 bg-cyan-500/10">
                <Icon name="History" size={18} color="#67e8f9" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white">Recover latest studio autosave?</p>
                <p className="mt-1 text-xs leading-relaxed text-gray-300">
                  {recoverySnapshot?.sessionTitle || 'Untitled Session'} saved locally
                  {recoverySnapshot?.savedAt ? ` at ${new Date(recoverySnapshot.savedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}.
                </p>
              </div>
            </div>
            <div className="flex shrink-0 gap-2">
              <Button onClick={discardRecoverySnapshot} disabled={recoveryBusy} className="bg-gray-800 hover:bg-gray-700 text-white">
                Discard
              </Button>
              <Button onClick={restoreRecoverySnapshot} disabled={recoveryBusy} className="bg-cyan-600 hover:bg-cyan-500 text-white">
                {recoveryBusy ? <span className="flex items-center gap-2"><Icon name="Loader" size={15} className="animate-spin" />Restoring</span> : 'Restore'}
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Save dialog */}
      {showSaveDialog && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-6 max-w-md w-full border border-gray-700">
            <h3 className="text-xl font-bold text-white mb-4">Save Session</h3>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">Session Title</label>
              <input type="text" value={sessionTitle} onChange={e => setSessionTitle(e?.target?.value)} placeholder="Enter session title" className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white" />
            </div>
            <div className="flex gap-3">
              <Button onClick={() => setShowSaveDialog(false)} disabled={saving} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white">Cancel</Button>
              <Button onClick={confirmSaveSession} disabled={saving} className="flex-1 bg-purple-600 hover:bg-purple-700 text-white">
                {saving ? <span className="flex items-center gap-2"><Icon name="Loader" size={16} className="animate-spin" />Saving...</span> : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RecordingStudio;
